import { defineComponent, computed, openBlock, createElementBlock, normalizeClass, createElementVNode, ref, watch, createBlock, unref, withCtx, createVNode, mergeProps, withModifiers, withKeys, renderSlot, withDirectives, normalizeStyle, normalizeProps, guardReactiveProps, vShow, createCommentVNode, resolveDynamicComponent, toDisplayString, resolveComponent, Fragment, renderList, cloneVNode, h, onMounted, onUnmounted, provide, toRaw, watchEffect, nextTick, inject, mergeModels, useModel, vModelCheckbox, createSlots, vModelText, vModelRadio } from 'vue';
import { Float, FloatArrow } from '@headlessui-float/vue';
import { OnClickOutside, UseElementBounding } from '@vueuse/components';
import { b as useI18n, a as useCalendar, c as useId, u as useBreakpoints } from './useBreakpoints-Cx1EETam.js';
import { d as deepCopy, a as useDayjs, s as safeIsEqual, u as useLocale } from './deepCopy-BPIPBkff.js';
import { e as escapeRegex, c as compose, d as deepConcat } from './escapeRegex-Bu1SqOvG.js';
import 'uuid';

const defaultLightTheme = {
  accordion: {
    container: "",
    accordionHeader: "accordion-header position-relative w-100% flex items-center border-1 border-gray-200 rounded border-rounded-b-0 px-4 py-2",
    accordionHeaderCollapsed: "!border-rounded-b",
    accordionHeaderIcon: "accordion-icon",
    accordionHeaderIconWrapper: "position-absolute right-4 flex items-center pl-1",
    accordionHeaderIconCollapsed: "i-far-chevron-down",
    accordionHeaderIconExpanded: "i-far-chevron-up",
    accordionHeaderSlotWrapper: "me-5",
    accordionHeaderSlotIconDefault: "accordion-icon",
    accordionBody: "accordion-body border-1 border-t-0 border-gray-200 rounded border-rounded-t-0 p-4"
  },
  accordionFormTemplate: {
    container: "",
    containerDisabled: "opacity-50 bg-surface-500 text-text-200",
    accordionHeader: "accordion-header transition-all duration-350 ease-in-out group flex flex-col border-1 rounded px-4 py-3",
    accordionHeaderExpanded: "bg-secondary-100 border-secondary-200 shadow-sm",
    accordionHeaderCollapsed: "bg-surface-200 hover:bg-surface-400 border-surface-700",
    accordionHeaderIconWrapper: "flex items-center flex-gap-3",
    accordionHeaderIcon: "i-fas-circle-check mr--1",
    accordionHeaderIconComplete: "bg-success grow-0",
    accordionHeaderIconIncomplete: "bg-surface-700",
    accordionHeaderTitle: "accordion-title group-hover:font-700 grow fw-600 font-sans",
    accordionFormWarningRequired: "text-sm fw-600 font-sans",
    accordionHeaderChipIcon: "i-fas-floppy-disk",
    accordionHeaderIconFont: "fw-900",
    accordionHeaderIconCollapsed: "i-far-chevron-down",
    accordionHeaderIconExpanded: "i-far-chevron-up",
    accordionHeaderSubtitle: "subtitle text-text-200 flex flex-row flex-gap-2 text-sm text-text-400 transition-opacity",
    accordionHeaderSubtitleFont: "mr-1 fw-600",
    accordionBody: "accordion-body mt-2 border-1 border-secondary-200 border-rounded bg-surface-200 p-6"
  },
  alertComponent: {
    container: "alert-container flex flex-col flex-gap-y-4 border-1 rounded p-4",
    warning: "bg-warning-100 border-warning",
    info: "bg-info-100 border-info",
    success: "bg-success-100 border-success",
    danger: "$reset bg-alert-100 border-alert",
    alertContentWrapper: "flex flex-gap-x-4 flex-gap-y-4 sm:flex-row",
    alertContent: "flex grow self-center items-start flex-gap-x-4 lt-sm:flex-gap-x-2.5",
    alertContentSlot: "text-sm text-text-500 translate-y-0.5",
    alertCtaSlotSmall: "lt-sm:hidden",
    alertCtaSlotLarge: "sm:hidden",
    alertCloseIcon: "i-far-xmark",
    alertCloseButton: "mb-auto ml-auto flex rounded-2px",
    alertWarningIcon: "i-fas-circle-exclamation",
    alertWarningHover: "hover:bg-warning",
    alertInfoIcon: "i-fas-circle-info",
    alertInfoHover: "hover:bg-info",
    alertSuccessIcon: "i-fas-circle-check",
    alertSuccessHover: "hover:bg-success",
    alertDangerIcon: "i-fas-circle-xmark",
    alertDangerHover: "hover:bg-danger"
  },
  asyncButton: {
    body: "flex flex-row items-center justify-center",
    spinner: "ms--3",
    text: "ms-4"
  },
  avatar: {
    container: "",
    avatarTemplate: "relative flex shrink-0 select-none items-center justify-center overflow-hidden bg-surface-300 bg-cover bg-clip-border bg-center",
    text: "m-auto font-serif fw-500 text-secondary",
    avatarTemplateSquare: "rounded",
    avatarTemplateHalfRounded: "rounded-1/2",
    avatarRingStyleLg: "ring-4",
    avatarRingStyleMd: "ring-3",
    avatarRingStyleDefault: "ring-1",
    avatarBorder: "ring-surface-600",
    avatarText: "m-auto font-serif fw-500 text-secondary",
    avatarImage: "position-absolute object-cover"
  },
  avatarList: {
    container: "avatars-list flex flex-row p-4",
    avatarListPerson: "avatar",
    avatarListDualAvatar: "pt-2",
    avatarListTooltip: "!w-auto mb-1",
    avatarListAvatar: "mx--1.5",
    avatarListInfo: "px-4 py-2 text-center text-xs fw-400",
    avatarListEmail: "fw-300",
    avatarListButtonLg: "-ml-4",
    avatarListButtonMd: "-ml-2",
    avatarListButtonSm: "-ml-1",
    avatarListAvatarClass: "fw-700",
    avatarListContent: "max-w-screen min-w-281px rounded-8px bg-surface-100 py-1 shadow-md ring-1 ring-surface-700",
    avatarListContentButton: "dropdown-list-item",
    avatarListContentAvatar: "me-2",
    avatarListAvatarDetails: "flex flex-col items-start",
    avatarListAvatarName: "text-sm text-text-500",
    avatarListAvatarEmail: "text-xs text-text-400"
  },
  backdrop: {
    container: "fixed inset-0 overflow-hidden bg-black/50"
  },
  badge: {
    container: "absolute right-0 top-0 transform-translate-x-50% transform-translate-y--50% text-xs"
  },
  breadcrumbs: {
    container: "",
    breadCrumbsNav: "ms--1 flex flex-row items-center justify-start lt-sm:!hidden",
    breadCrumbsChevron: "i-far-chevron-right mx-1 p-1 text-3 text-secondary-200",
    breadCrumbsMainCrumb: "breadcrumb cursor-pointer rounded-5px px-2 py-1 text-xs lh-16px text-secondary-300 active:bg-surface-500 active:text-secondary focus:bg-surface-500 focus:text-secondary hover:bg-surface-500 hover:text-secondary last:bg-surface-500 last:text-secondary !no-underline",
    breadCrumbsPenultimateCrumbNav: "ms--2 sm:hidden",
    breadCrumbsPenultimateCrumb: "breadcrumb flex-inline flex-row items-center self-start justify-start rounded-5px px-2 py-1 active:bg-surface-500 active:text-secondary focus:bg-surface-500 focus:text-secondary hover:bg-surface-500 hover:text-secondary last:bg-surface-500 last:text-secondary !no-underline",
    breadCrumbsIconWrapper: "icon-box me-1 h-4 w-4 flex items-center justify-center",
    breadCrumbsIcon: "i-far-chevron-left mx-1 p-1 text-3 text-secondary-200",
    breadCrumbsPenultimateCrumbLabel: "text-xs lh-16px text-secondary-300"
  },
  button: {
    container: "cursor-pointer lt-sm:w-full"
  },
  calendar: {
    container: "w-276px",
    calendarContent: "calendar-body flex flex-col flex-gap-y-2px",
    calendarWeekdays: "calendar-weekdays flex flex-row flex-gap-x-1 text-xs fw-semibold text-text-300",
    calendarWeekdayText: "calendar-weekday w-36px self-center px-1 py-2 text-center",
    calendarWeeks: "calendar-week flex flex-row flex-gap-x-1 text-xs fw-semibold text-text-300",
    calendarDay: "calendar-day",
    calendarDayButton: "calendar-day if-active:bg-secondary-100 !if-disabled:text-text-200 !if-disabled:bg-white size-36px inline-flex flex-1 select-none items-center justify-center rounded-1/2 p-2 text-center text-sm fw-bold text-text-500 hover:bg-secondary-100",
    calendarDateUnderLined: "!w-full !border-b-1",
    calendarDateNotUnderlinedSelected: "!border-primary-100",
    calendarDateNotUnderlined: "!border-primary-400",
    calendarMonthWrapper: "base-month",
    // Base classes need to be required in component
    calendarMonth: "base-month-body mb-4 flex items-center justify-between",
    calendarPreviousMonth: "calendar-month-prev h-24px w-24px",
    calendarPreviousMonthIcon: "i-fas-arrow-left",
    calendarPreviousMonthIconStandard: "text-secondary-100",
    calendarPreviousMonthIconMore: "text-secondary-400",
    calendarMonthYear: "base-calendar-month-year flex flex-gap-x-2",
    calendarNextMonth: "calendar-month-prev h-24px w-24px",
    calendarNextMonthIcon: "i-fas-arrow-right",
    calendarNextMonthIconStandard: "text-secondary-100",
    calendarNextMonthIconMore: "text-secondary-400"
  },
  calendarInput: {
    container: ""
  },
  card: {
    container: "",
    cardInnerContainer: "flex flex-col flex-gap-y-4 border-1 p-4",
    cardImageContainer: "overflow-hidden",
    cardHeading: "text-lg font-semibold"
  },
  checkbox: {
    container: "",
    checkbox: "cursor-pointer",
    checkboxButton: "inline-block"
  },
  chip: {
    container: "w-min flex flex-row items-center justify-center whitespace-nowrap border-1 rounded-full px-2 py-0.5 text-xs text-text-500",
    alert: "bg-alert-100 border-alert",
    danger: "bg-danger-100 border-danger",
    info: "bg-info-100 border-info",
    success: "bg-success-100 border-success",
    warning: "bg-warning-100 border-warning",
    utility: "bg-utility-100 border-utility",
    primary: "bg-primary-100 border-primary",
    secondary: "bg-secondary-100 border-secondary",
    tertiary: "bg-tertiary-100 border-tertiary",
    surface: "bg-surface-100 border-surface-700",
    chipIcon: "mr-1.5 leading-0"
  },
  clipboard: {
    clipboardIcon: "ds-clipboard-button__icon i-far-copy duration-150 hover:i-fas-copy",
    clipboardLabel: "ds-clipboard-button__label mb-0.5 ml-2 text-sm",
    clipboardCopied: "p-1 color-text-button",
    clipboardCopiedLight: "text-secondary"
  },
  collapse: {
    container: "block overflow-hidden transition-max-height duration-500 ease"
  },
  comboboxInput: {
    container: "h-40px overflow-hidden",
    enter: "transition duration-100 ease-out",
    enterFrom: "transform scale-95 opacity-0",
    enterTo: "transform scale-100 opacity-100",
    leave: "transition duration-75 ease-out",
    leaveFrom: "transform scale-100 opacity-100",
    leaveTo: "transform scale-95 opacity-0",
    field: "relative h-40px flex rounded bg-white ring-1 ring-surface-700 ring-inset shadow-reset",
    fieldLayout: "me-8 w-full flex flex-row cursor-default items-center truncate rounded px-4 py-1 text-left text-sm",
    input: "w-full inline-flex text-gray-900 ring-transparent lt-sm:text-sm !outline-none",
    chevronDownButton: "absolute inset-y-0 right-0 flex items-center pr-4",
    spinner: "pr-4 text-text-300",
    valueBox: "absolute inset-1 z-1 flex flex-row items-center justify-between rounded bg-white px-3 py-1",
    valueBoxLabel: "block flex flex-row items-center truncate lt-sm:text-sm",
    closeButton: "touch-target ms-3 flex after:h-36px",
    menu: "max-h-60 min-w-[inherit] flex flex-col flex-gap-1 overflow-auto rounded bg-white py-1 text-base shadow ring-1 ring-surface-700 lt-sm:text-sm focus:outline-none",
    noResultLabel: "relative cursor-default select-none px-4 py-2 text-gray-700",
    optionItem: "combobox-option cursor-default select-none select-none text-text-500 hover:!cursor-pointer",
    optionActive: "active text-secondary-400 bg-surface-500",
    optionDisabled: "disabled text-text-200 bg-white",
    optionCustom: "block flex flex-row items-center truncate px-4 py-1"
  },
  dataPager: {
    container: "flex",
    dataPagerList: "flex rounded bg-white shadow-sm",
    dataPagerListItem: "flex",
    dataPagerFirstPageButton: "select-none border border-r-0 border-gray-100 rounded-l px-3 py-2 text-sm text-gray-600 no-underline hover:bg-surface-500 disabled:text-text-200",
    dataPagerLastPageButton: "select-none border border-l-0 border-gray-100 rounded-r px-3 py-2 text-sm text-gray-600 no-underline hover:bg-surface-500 disabled:text-text-200",
    dataPagerChevronLeft: "i-far-chevron-left",
    dataPagerChevronRight: "i-far-chevron-right",
    dataPagerPageLabel: "select-none border border-surface-700 bg-surface-500 px-3 py-2 text-sm color-text-heading no-underline hover:no-underline",
    dataPagerPageLink: "border border-surface-500 px-3 py-2 text-sm color-text-label hover:bg-surface-500 hover:no-underline"
  },
  dataPagerSummary: {
    container: "text-sm color-text-400",
    dataPagerSummaryText: "font-bold"
  },
  dataSorter: {
    container: "touch-target relative ms-5 h-16px w-16px flex",
    dataSorterIcon: "m-auto self-center text-12px text-secondary-300",
    dataSorterIconInactive: "i-fas-sort",
    dataSorterIconAsc: "i-fas-sort-up",
    dataSorterIconDesc: "i-fas-sort-down",
    dataSorterBadge: "flex items-center justify-center b-1 b-rounded-1/2 bg-surface-600 p-2px",
    dataSorterBadgeIcon: "text-10px",
    dataSorterBadgeIcon1: "i-fas-1",
    dataSorterBadgeIcon2: "i-fas-2",
    dataSorterBadgeIcon3: "i-fas-3",
    dataSorterBadgeIcon4: "i-fas-4",
    dataSorterBadgeIcon5: "i-fas-5",
    dataSorterBadgeIcon6: "i-fas-6",
    dataSorterBadgeIcon7: "i-fas-7",
    dataSorterBadgeIcon8: "i-fas-8",
    dataSorterBadgeIcon9: "i-fas-9"
  },
  dateCalendarInput: {
    container: "input-date",
    dateCalendarInputEnter: "transition duration-100 ease-in-out",
    dateCalendarInputEnterFrom: "transform scale-95 opacity-0",
    dateCalendarInputEnterTo: "transform scale-100 opacity-100",
    dateCalendarInputLeave: "transition duration-75 ease-in-out",
    dateCalendarInputLeaveFrom: "transform scale-100 opacity-100",
    dateCalendarInputLeaveTo: "transform scale-95 opacity-0",
    dateCalendarInputCalendarIconButton: "flex grow items-center b-1 border-surface-700 border-rd bg-white px-3 py-2.5",
    dateCalendarInputCalendarIcon: "i-far-calendar mr-2 text-lg text-secondary-300",
    dateCalendarInputTimeWrapper: "position-relative text-sm text-text-300",
    dateCalendarInputTime: "position-relative z-1 select-none whitespace-nowrap",
    dateCalendarInputContent: "w-fit rounded bg-white p-4 shadow ring-1 ring-surface-700",
    dateCalendarInputButtonRow: "mt-4 flex flex-gap-x-2",
    dateCalendarInputConfirm: "flex-1 btn-sm btn-fill-secondary",
    dateCalendarInputCancel: "flex-1 ring-1 btn-sm btn-outline-secondary"
  },
  dateRangeInput: {
    enterActive: "transition transform duration-100 ease-out",
    enterFrom: "opacity-0 scale-90",
    enterTo: "opacity-100 scale-100",
    leaveActive: "transition transform duration-100 ease-in",
    leaveFrom: "opacity-100 scale-100",
    leaveTo: "opacity-0 scale-90",
    openButton: "h-40px max-w-full flex items-center justify-between flex-gap-x-1 rounded rounded-4px bg-white px-3 py-2 text-sm ring-1 ring-surface-700 ring-inset shadow-reset focus:outline-none",
    displayValueDetail: "mt-1 text-left text-xs text-text-500",
    displayValue: "block truncate text-gray-300 capitalize text-text-500",
    menu: "rounded bg-white shadow-lg ring-1 ring-surface-700 ring-inset lt-sm:w-235px sm:w-327px relative",
    menuLayout: "flex flex-col overflow-hidden p-4",
    menuName: "mb-2 fw-semibold",
    applyButton: "mt-6 btn-sm text-center btn-fill-secondary",
    rangeForm: "flex flex-col flex-gap-y-4",
    rangeFormLast: "flex flex-row flex-gap-x-2",
    rangeFormLastUnit: "w-150px",
    rangeFormLastUnitOptions: "lt-sm:max-w-20",
    rangeFormLastQuantityWrapper: "max-w-61px",
    rangeFormBetween: "flex justify-between flex-gap-2 sm:flex-row sm:items-center lt-sm:flex-col lt-sm:items-stretch",
    rangeFormBetweenCalendarInput: "self-stretch",
    rangeFormBetweenCalendarInputError: "!border-danger",
    rangeFormBetweenAnd: "text-center text-sm"
  },
  debugInfo: {
    container: "mt-4 flex flex-col flex-gap-4 border-1 border-surface-600 rounded bg-surface-200 p-3 text-xs text-text-500",
    debugInfoTitle: "border-b-1 border-surface-600 fw-semibold text-text-400",
    debugInfoOptions: "flex flex-row flex-wrap flex-gap-2 text-xs",
    debugInfoSingleOption: "border-1 border-surface-600 rounded bg-surface-500 p-1 px-2",
    debugInfoSingleOptionLabel: "flex flex-gap-x-1",
    debugInfoDisplayedValues: "fw-700"
  },
  dropdown: {
    container: "ds-dropdown",
    dropdownMenuButton: "ds-dropdown-button min-h-40px flex flex-row select-none items-center justify-between flex-gap-x-4 truncate",
    dropdownLabelSlot: "truncate",
    dropdownIconSlot: "ds-dropdown-icon i-far-chevron-down flex-shrink-0 text-sm",
    dropdownEnterActive: "transition transform duration-100 ease-out",
    dropdownEnterFrom: "opacity-0 scale-90",
    dropdownEnterTo: "opacity-100 scale-100",
    dropdownLeaveActive: "transition transform duration-100 ease-in",
    dropdownLeaveFrom: "opacity-100 scale-100",
    dropdownLeaveTo: "opacity-0 scale-90",
    dropdownContent: "relative",
    dropdownList: "max-w-250px min-w-150px overflow-hidden rounded-6px bg-white shadow-lg ring-1 ring-surface-700",
    dropdownMenuItems: "w-full py-1",
    dropdownMenuSingleItem: "w-full px-4 py-2 text-left text-sm text-text-500 hover:bg-surface-500",
    dropdownMenuItemActive: "bg-surface-500"
  },
  icon: {
    container: "inline-flex shrink-0 items-center justify-center",
    iconSizeXs: "wh-16px text-12px",
    iconSizeSm: "wh-18px text-16px",
    iconSizeMd: "wh-24px text-18px",
    iconSizeLg: "wh-32px text-24px",
    iconSizeXl: "wh-48px text-36px"
  },
  listboxInput: {
    container: "!block",
    listboxInputEnter: "transition duration-100 ease-out",
    listboxInputEnterFrom: "transform scale-95 opacity-0",
    listboxInputEnterTo: "transform scale-100 opacity-100",
    listboxInputLeave: "transition duration-75 ease-out",
    listboxInputLeaveFrom: "transform scale-100 opacity-100",
    listboxInputLeaveTo: "transform scale-95 opacity-0",
    listboxInputButton: "listbox-button h-40px w-full flex flex-row cursor-default items-center justify-between flex-gap-x-2 rounded bg-white py-2 text-left shadow-none lt-sm:text-sm",
    listboxInputButtonRinged: "px-4 ring-1 ring-surface-700 ring-inset",
    listboxInputButtonRingless: "px-0",
    listboxInputButtonContent: "block truncate",
    listboxInputButtonContentEmpty: "text-gray-400",
    listboxInputButtonIcon: "pointer-events-none ms-3 flex items-center",
    listboxInputButtonIconDefault: "i-far-chevron-down text-lg text-secondary",
    listboxInputOptions: "listbox-options max-h-60 min-w-[inherit] flex flex-col flex-gap-1 overflow-auto rounded bg-white py-1 text-base shadow ring-1 ring-surface-700 lt-sm:text-sm focus:outline-none",
    listboxInputSingleOption: "cursor-default select-none text-text-500 disabled:text-text-200 bg-white",
    listboxInputOptionSelected: "text-secondary-400 bg-surface-600 fw-medium",
    listboxInputOptionActive: "text-secondary-400 bg-surface-500",
    listboxInputOptionSlot: "flex flex-row items-center px-3 py-2",
    listboxInputOptionSlotLabel: "block truncate"
  },
  loadingWrapper: {
    container: "bg-white/75"
  },
  modal: {
    container: "ds-modal z-modal fixed bottom-0 left-0 right-0 top-0 flex lt-sm:mx-4 lt-sm:my-4 sm:mx-auto sm:mt-10",
    modalEnter: "duration-300 ease-out",
    modalEnterFrom: "opacity-0",
    modalEnterTo: "opacity-100",
    modalLeave: "duration-200 ease-in",
    modalLeaveFrom: "opacity-100",
    modalLeaveTo: "opacity-0",
    modalDialogOverlay: "dialog-overlay position-fixed bottom-0 left-0 right-0 top-0 bg-gray-400 opacity-50",
    modalLoadingEnter: "duration-300 ease-out",
    modalLoadingEnterFrom: "opacity-0 scale-95",
    modalLoadingEnterTo: "opacity-100 scale-100",
    modalLoadingLeave: "duration-200 ease-in",
    modalLoadingLeaveFrom: "opacity-100 scale-100",
    modalLoadingLeaveTo: "opacity-0 scale-95",
    modalDialogPanel: "position-relative m-auto border-1 border-gray-400 rounded-4px bg-surface-200 shadow-lg focus:outline-0",
    modalClose: "btn-close position-absolute right-6 top-4 box-border flex border-0 rounded bg-transparent text-8",
    modalCloseIcon: "i-far-xmark text-gray-800"
  },
  notifier: {
    container: "flex flex-col flex-gap-y-4",
    notifierEnter: "transition-opacity,transform duration-500",
    notifierEnterFrom: "opacity-0 translate-x-500px",
    notifierEnterTo: "opacity-100 translate-x-0",
    notifierLeave: "transition-opacity,transform duration-1000",
    notifierLeaveFrom: "opacity-100 translate-x-0",
    notifierLeaveTo: "opacity-0 translate-x-500px",
    danger: "$reset bg-alert-100 border-alert",
    info: "bg-info-100 border-info",
    success: "bg-success-100 border-success",
    warning: "bg-warning-100 border-warning",
    utility: "bg-utility-100 border-utility",
    notifierToastWrapper: "toast border-1 rounded shadow-sm",
    notifierToast: "toast-wrapper p-4 pb-6",
    notifierDangerIcon: "absolute right-4",
    notifierToastHeader: "toast-header flex justify-between",
    notifierTitleIconBoxWrapper: "flex",
    notifierTitleIconBox: "icon-box h-24px w-24px flex flex-shrink-0 items-center self-start justify-center",
    notifierToastClose: "i-far-xmark",
    notifierTitleIconDanger: "i-fas-circle-xmark",
    notifierTitleIconInfo: "i-fas-circle-info",
    notifierTitleIconSuccess: "i-fas-circle-check",
    notifierTitleIconWarning: "i-fas-circle-exclamation",
    notifierTitleIconUtility: "i-fas-square-info",
    notifierTitleIcon: "m-auto text-18px color-text-500",
    notifierTitle: "pl-2 font-serif text-base font-medium color-text-500",
    notifierContent: "mt-2 font-sans text-lg color-text-500"
  },
  numberInput: {
    numberInputContainer: "flex flex-col items-center",
    numberInputButton: "h-40px w-full flex items-center justify-center",
    numberInputInput: "h-40px w-52px flex flex-row cursor-default items-center justify-between flex-gap-x-2 rounded bg-white px-2 py-2 text-center shadow-none ring-1 ring-surface-700 ring-inset lt-sm:text-sm"
  },
  offCanvas: {
    container: "",
    offCanvasEnter: "transition-transform,width ease-in-out duration-350",
    offCanvasEnterFrom: "translate-x--100%",
    offCanvasEnterTo: "translate-x-0",
    offCanvasLeave: "transition-transform,width ease-in-out duration-350",
    offCanvasLeaveFrom: "translate-x-0",
    offCanvasLeaveTo: "translate-x--100%",
    offCanvasContent: "offcanvas-inner fixed bottom-0 left-0 top-0 z-201 w-fit flex flex-col overflow-hidden b-r-1 b-black/50 shadow-lg"
  },
  personComboboxInput: {
    optionLayout: "flex flex-row items-center",
    optionLabel: "flex flex-col",
    optionName: "text-sm text-text-500",
    optionEmail: "text-xs text-text-400"
  },
  popup: {
    container: "inline-block",
    popupButton: "ds-popup-button-wrapper"
  },
  progressBar: {
    container: "border-box m0 flex flex-col items-center p0 text-14px text-#606266",
    text: "font-base mt-2 font-serif text-left inline-block ml-10px",
    outer: "border-box vertical-align-middle inline-block w-full",
    outerLine: "rounded-xl bg-brand relative rounded-100px transition-property-all transition-duration-750 transition-ease transition-delay-0",
    outerLineActive: "bg-white rounded-10px h-inherit align-middle break-keep opacity-20",
    barBg: "rounded-xl bg-#EBEDF0"
  },
  radio: {
    container: "cursor-pointer"
  },
  rating: {
    container: "w-fit cursor-pointer px-8 py-4 text-brand",
    ratingStar: "i-fas-star-sharp",
    ratingStarL: "i-fal-star-sharp",
    ratingHalfStar: "i-fas-star-sharp-half-stroke"
  },
  spinner: {
    container: "",
    spinnerInline: "spinner-inline",
    spinnerBlock: "spinner-block absolute bottom-0 left-0 right-0 top-0 w-full flex",
    spinnerBlockInner: "h-full w-full"
  },
  switch: {
    container: "flex",
    switchButtonWrapper: "flex items-center flex-gap-x-2",
    switchButtonWrapperReverse: "flex-row-reverse",
    switchButton: "base-switch s-2 relative h-5 w-9 inline-flex shrink-0 items-center rounded-full ring-1 transition-colors transition-colors",
    switchButtonChecked: "bg-success-400 ring-success-400",
    switchButtonUnchecked: "bg-surface-500 ring-surface-700",
    switchToggle: "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
    switchToggleChecked: "translate-x-4.5",
    switchToggleUnchecked: "translate-x-0.5 ring-1 ring-inset ring-surface-700",
    switchLabel: "cursor-pointer"
  },
  stepper: {
    containerMobile: "mobile sm:hidden",
    currentStepMobileWrapper: "flex flex-row items-center flex-gap-x-2 rounded bg-surface-200 p-2 ring-1 ring-surface-600",
    currentStepMobile: "max-w-1/2 text-xs text-secondary fw-semibold",
    progressBarMobileWrapper: "progress-bar relative h-6px flex-grow",
    progressBarMobile: "absolute h-full w-full rounded bg-surface-600",
    currentStepIndexMobile: "absolute h-full rounded bg-secondary-300",
    currentStepIndexMobileComplete: "w-full",
    currentStepIndexMobileIncomplete: "w-38",
    roundedMobile: "rounded-44px bg-secondary-100 px-2 text-xs fw-semibold ring-1 ring-secondary-200",
    containerDesktop: "mobile lt-sm:hidden",
    stepsWrapper: "flex gap-x-3",
    steps: "step flex flex-grow flex-col gap-y-2.5",
    stepsCircleWrapper: "flex flex-grow flex-row items-center",
    stepsCircle: "circle me-4 h-8 w-8 flex flex-shrink-0 items-center justify-center rounded-full ring-1 ring-inset",
    stepsCircleWithBgSecondary: "bg-secondary-100 ring-secondary-200",
    stepsCircleWithBgSurface: "bg-surface-500 ring-surface-600",
    stepsCircleInner: "circle-inner h-4 w-4 flex items-center justify-center",
    stepsRounded: "h-3 w-3 rounded-full bg-surface-600",
    stepsRoundedIncomplete: "h-3 w-3 rounded-full bg-secondary-200",
    stepsRoundedComplete: "i-far-check text-xs text-secondary-300",
    smallText: "text-xs font-semibold",
    smallTextMissing: "text-text-200",
    smallTextIncomplete: "text-secondary-300",
    smallTextComplete: "text-secondary",
    progressBar: "progress-bar relative h-1.5",
    rounded: "absolute h-full w-full rounded bg-surface-600",
    roundedInner: "absolute h-full rounded bg-secondary-300",
    roundedInnerIncomplete: "w-9",
    roundedInnerComplete: "w-full"
  },
  tabBar: {
    container: "w-100% border-1 border-surface-700 border-rd",
    tabBarTwoOptions: "flex pa-2 flex-nowrap overflow-auto wa justify-between flex-gap-1",
    tabBarSingleOptions: "flex pa-2 flex-nowrap overflow-auto",
    tabBarTabTwoOptions: "w-100%",
    tabBarTabActive: "bg-surface-600 !fw-600 !text-secondary",
    tabBarButtonDisabled: "mr-1 flex items-center truncate border-rd pa-2 px-3 text-sm fw-500 color-text-200",
    tabBarButtonTwoOptions: "pa-2 w-100% justify-center align-center",
    tabBarButtonDisabledIcon: "mr-2",
    tabBarButton: "mr-1 flex items-center truncate border-rd pa-2 px-3 text-sm fw-500 color-text-400 hover:bg-surface-300 hover:underline",
    tabBarButtonLabelTwoOptions: "text-center",
    tabBarButtonIcon: "mr-2"
  },
  table: {
    container: "w-full border-separate border-spacing-0 ring-0.5px ring-surface-600 rounded-2 first:[&_th]:first:[&_tr]:rounded-tl-2 first:[&_td]:first:[&_tr]:rounded-tl-2 last:[&_th]:first:[&_tr]:rounded-tr-2 last:[&_td]:first:[&_tr]:rounded-tr-2 first:[&_th]:last:[&_tr]:rounded-bl-2 first:[&_td]:last:[&_tr]:rounded-bl-2 last:[&_th]:last:[&_tr]:rounded-br-2 last:[&_td]:last:[&_tr]:rounded-br-2",
    table: "w-full",
    tableContainerScrollable: "overflow-x-auto",
    tableScrollable: "mb-3 border-b-1 border-surface-700 rounded",
    tableStriped: "striped"
  },
  tableCell: {
    container: "p-4 text-sm text-text-500 ring-0.5px ring-inset ring-surface-600",
    cellContainer: "flex items-center justify-between"
  },
  tableHeader: {
    container: "bg-secondary-100 p-4 text-start h5 ring-0.5px ring-inset ring-surface-600 shadow-lg top-0 z-10",
    tableHeader: "flex items-center justify-between whitespace-nowrap left-0 z-2"
  },
  tableRow: {
    container: "odd:bg-surface-300 even:bg-white"
  },
  timeInput: {
    timeInputEnter: "transition duration-100 ease-in-out",
    timeInputEnterFrom: "transform scale-95 opacity-0",
    timeInputEnterTo: "transform scale-100 opacity-100",
    timeInputLeave: "transition duration-75 ease-in-out",
    timeInputLeaveFrom: "transform scale-100 opacity-100",
    timeInputLeaveTo: "transform scale-95 opacity-0",
    timeInputDisplay: "flex items-center justify-center",
    timeInputContainer: "w-full",
    timeInputContent: "w-fit rounded bg-white px-4 py-2 shadow ring-1 ring-surface-700 flex items-center justify-center gap-4",
    timeInputNumberInputContainer: "flex items-center justify-center gap-1",
    timeInputPeriodContainer: "flex flex-col gap-2",
    timeInputPeriodButton: "btn-sm min-h-30px min-w-40px flex items-center justify-center p-0",
    timeInputPeriodButtonInactive: "btn-fill-secondary",
    timeInputPeriodButtonActive: "btn-outline-surface-700 text-text-500"
  },
  tooltip: {
    container: "ds-tooltip flex",
    tooltipButton: "max-w-fit inline-flex",
    tooltipButtonIcon: "self-center",
    tooltipButtonSlotDefault: "i-far-circle-info inline-flex self-center",
    tooltipButtonSlotDefaultSmallText: "text-sm m-2px",
    tooltipButtonSlotDefaultLargeText: "text-lg m-3px",
    tooltip: "max-w-295px rounded rounded-6px font-sans text-xs fw-400 drop-shadow",
    tooltipArrow: "absolute z--1 h-5 w-5 rotate-45 rounded",
    tooltipBorder: "border-2",
    tooltipSlot: "relative overflow-hidden",
    tooltipContent: "rounded px-4 py-2",
    // Tooltip has computed color classes controlled by props
    tooltipBgColorClassComputedLight: "bg-secondary-100",
    tooltipBgColorClassComputedDark: "bg-secondary",
    tooltipBorderColorClassComputed: "border-secondary-300",
    tooltipBorderColorClassComputedDefault: "border-transparent",
    tooltipTextColorClassComputedLight: "text-secondary",
    tooltipTextColorClassComputedDark: "text-white"
  },
  dataTable: {
    container: "",
    dataTableScrollableContainer: "scrollbar-track-color-white scrollbar-h-7px scrollbar-radius-4 scrollbar-w-7px scrollbar scrollbar-rounded relative max-h-90vh w-full ring-0.5px ring-inset ring-surface-600 rounded",
    dataTableScrollable: "!overflow-x-scroll mb-0.5 mr-0.5 max-w-[calc(100vw-64px)]",
    dataTableLoadingWrapper: "min-h-400px",
    dataTableSelectableCell: "p-4 left-0 z-2 w-0%"
  }
};

const globalConfig = {};
const globalConfigDefaults = {
  theme: defaultLightTheme
};
function setGlobalConfig(config) {
  Object.assign(globalConfig, {
    ...globalConfigDefaults,
    ...config
  });
}
setGlobalConfig({});

const emptyTheme = {
  accordion: {
    container: "",
    accordionHeader: "",
    accordionHeaderCollapsed: "",
    accordionHeaderIcon: "",
    accordionHeaderIconWrapper: "",
    accordionHeaderIconCollapsed: "",
    accordionHeaderIconExpanded: "",
    accordionHeaderSlotWrapper: "",
    accordionHeaderSlotIconDefault: "",
    accordionBody: ""
  },
  alertComponent: {
    container: "",
    warning: "",
    info: "",
    success: "",
    danger: "",
    alertContentWrapper: "",
    alertContent: "",
    alertContentSlot: "",
    alertCtaSlotSmall: "",
    alertCtaSlotLarge: "",
    alertCloseIcon: "",
    alertCloseButton: "",
    alertWarningIcon: "",
    alertWarningHover: "",
    alertInfoIcon: "",
    alertInfoHover: "",
    alertSuccessIcon: "",
    alertSuccessHover: "",
    alertDangerIcon: "",
    alertDangerHover: ""
  },
  asyncButton: {
    body: "",
    spinner: "",
    text: ""
  },
  avatar: {
    container: "",
    avatarTemplate: "",
    text: "",
    avatarTemplateSquare: "",
    avatarTemplateHalfRounded: "",
    avatarRingStyleLg: "",
    avatarRingStyleMd: "",
    avatarRingStyleDefault: "",
    avatarBorder: "",
    avatarText: "",
    avatarImage: ""
  },
  avatarList: {
    container: "",
    avatarListPerson: "",
    avatarListDualAvatar: "",
    avatarListTooltip: "",
    avatarListAvatar: "",
    avatarListInfo: "",
    avatarListEmail: "",
    avatarListButtonLg: "",
    avatarListButtonMd: "",
    avatarListButtonSm: "",
    avatarListAvatarClass: "",
    avatarListContent: "",
    avatarListContentButton: "",
    avatarListContentAvatar: "",
    avatarListAvatarDetails: "",
    avatarListAvatarName: "",
    avatarListAvatarEmail: ""
  },
  backdrop: {
    container: ""
  },
  badge: {
    container: ""
  },
  breadcrumbs: {
    container: "",
    breadCrumbsNav: "",
    breadCrumbsChevron: "",
    breadCrumbsMainCrumb: "",
    breadCrumbsPenultimateCrumbNav: "",
    breadCrumbsPenultimateCrumb: "",
    breadCrumbsIconWrapper: "",
    breadCrumbsIcon: "",
    breadCrumbsPenultimateCrumbLabel: ""
  },
  button: {
    container: ""
  },
  calendar: {
    container: "",
    calendarContent: "",
    calendarWeekdays: "",
    calendarWeekdayText: "",
    calendarWeeks: "",
    calendarDay: "",
    calendarDayButton: "",
    calendarDateUnderLined: "",
    calendarDateNotUnderlinedSelected: "",
    calendarDateNotUnderlined: "",
    calendarMonthWrapper: "",
    calendarMonth: "",
    calendarPreviousMonth: "",
    calendarPreviousMonthIcon: "",
    calendarPreviousMonthIconStandard: "",
    calendarPreviousMonthIconMore: "",
    calendarMonthYear: "",
    calendarNextMonth: "",
    calendarNextMonthIcon: "",
    calendarNextMonthIconStandard: "",
    calendarNextMonthIconMore: ""
  },
  calendarInput: {
    container: ""
  },
  card: {
    container: "",
    cardInnerContainer: "",
    cardImageContainer: "",
    cardHeading: ""
  },
  checkbox: {
    container: "",
    checkbox: "",
    checkboxButton: ""
  },
  chip: {
    container: "",
    alert: "",
    danger: "",
    info: "",
    success: "",
    warning: "",
    utility: "",
    primary: "",
    secondary: "",
    tertiary: "",
    surface: "",
    chipIcon: ""
  },
  clipboard: {
    clipboardIcon: "",
    clipboardLabel: "",
    clipboardCopied: "",
    clipboardCopiedLight: ""
  },
  collapse: {
    container: ""
  },
  comboboxInput: {
    container: "",
    enter: "",
    enterFrom: "",
    enterTo: "",
    leave: "",
    leaveFrom: "",
    leaveTo: "",
    field: "",
    fieldLayout: "",
    input: "",
    chevronDownButton: "",
    spinner: "",
    valueBox: "",
    valueBoxLabel: "",
    closeButton: "",
    menu: "",
    noResultLabel: "",
    optionItem: "",
    optionActive: "",
    optionDisabled: "",
    optionCustom: ""
  },
  dataPager: {
    container: "",
    dataPagerList: "",
    dataPagerListItem: "",
    dataPagerFirstPageButton: "",
    dataPagerLastPageButton: "",
    dataPagerChevronLeft: "",
    dataPagerChevronRight: "",
    dataPagerPageLabel: "",
    dataPagerPageLink: ""
  },
  dataPagerSummary: {
    container: "",
    dataPagerSummaryText: ""
  },
  dataSorter: {
    container: "",
    dataSorterIcon: "",
    dataSorterIconInactive: "",
    dataSorterIconAsc: "",
    dataSorterIconDesc: "",
    dataSorterBadge: "",
    dataSorterBadgeIcon: "",
    dataSorterBadgeIcon1: "",
    dataSorterBadgeIcon2: "",
    dataSorterBadgeIcon3: "",
    dataSorterBadgeIcon4: "",
    dataSorterBadgeIcon5: "",
    dataSorterBadgeIcon6: "",
    dataSorterBadgeIcon7: "",
    dataSorterBadgeIcon8: "",
    dataSorterBadgeIcon9: ""
  },
  dateCalendarInput: {
    container: "",
    dateCalendarInputEnter: "",
    dateCalendarInputEnterFrom: "",
    dateCalendarInputEnterTo: "",
    dateCalendarInputLeave: "",
    dateCalendarInputLeaveFrom: "",
    dateCalendarInputLeaveTo: "",
    dateCalendarInputCalendarIconButton: "",
    dateCalendarInputCalendarIcon: "",
    dateCalendarInputTimeWrapper: "",
    dateCalendarInputTime: "",
    dateCalendarInputContent: "",
    dateCalendarInputButtonRow: "",
    dateCalendarInputConfirm: "",
    dateCalendarInputCancel: ""
  },
  dateRangeInput: {
    enterActive: "",
    enterFrom: "",
    enterTo: "",
    leaveActive: "",
    leaveFrom: "",
    leaveTo: "",
    openButton: "",
    displayValueDetail: "",
    displayValue: "",
    menu: "",
    menuLayout: "",
    menuName: "",
    applyButton: "",
    rangeForm: "",
    rangeFormLast: "",
    rangeFormLastUnit: "",
    rangeFormLastUnitOptions: "",
    rangeFormLastQuantityWrapper: "",
    rangeFormBetween: "",
    rangeFormBetweenCalendarInput: "",
    rangeFormBetweenCalendarInputError: "",
    rangeFormBetweenAnd: ""
  },
  debugInfo: {
    container: "",
    debugInfoTitle: "",
    debugInfoOptions: "",
    debugInfoSingleOption: "",
    debugInfoSingleOptionLabel: "",
    debugInfoDisplayedValues: ""
  },
  dropdown: {
    container: "",
    dropdownMenuButton: "",
    dropdownLabelSlot: "",
    dropdownIconSlot: "",
    dropdownEnterActive: "",
    dropdownEnterFrom: "",
    dropdownEnterTo: "",
    dropdownLeaveActive: "",
    dropdownLeaveFrom: "",
    dropdownLeaveTo: "",
    dropdownContent: "",
    dropdownList: "",
    dropdownMenuItems: "",
    dropdownMenuSingleItem: "",
    dropdownMenuItemActive: ""
  },
  icon: {
    container: "",
    iconSizeXs: "",
    iconSizeSm: "",
    iconSizeMd: "",
    iconSizeLg: "",
    iconSizeXl: ""
  },
  listboxInput: {
    container: "",
    listboxInputEnter: "",
    listboxInputEnterFrom: "",
    listboxInputEnterTo: "",
    listboxInputLeave: "",
    listboxInputLeaveFrom: "",
    listboxInputLeaveTo: "",
    listboxInputButton: "",
    listboxInputButtonRinged: "",
    listboxInputButtonRingless: "",
    listboxInputButtonContent: "",
    listboxInputButtonContentEmpty: "",
    listboxInputButtonIcon: "",
    listboxInputButtonIconDefault: "",
    listboxInputOptions: "",
    listboxInputSingleOption: "",
    listboxInputOptionSelected: "",
    listboxInputOptionActive: "",
    listboxInputOptionSlot: "",
    listboxInputOptionSlotLabel: ""
  },
  loadingWrapper: {
    container: ""
  },
  modal: {
    container: "",
    modalEnter: "",
    modalEnterFrom: "",
    modalEnterTo: "",
    modalLeave: "",
    modalLeaveFrom: "",
    modalLeaveTo: "",
    modalDialogOverlay: "",
    modalLoadingEnter: "",
    modalLoadingEnterFrom: "",
    modalLoadingEnterTo: "",
    modalLoadingLeave: "",
    modalLoadingLeaveFrom: "",
    modalLoadingLeaveTo: "",
    modalDialogPanel: "",
    modalClose: "",
    modalCloseIcon: ""
  },
  notifier: {
    container: "",
    notifierEnter: "",
    notifierEnterFrom: "",
    notifierEnterTo: "",
    notifierLeave: "",
    notifierLeaveFrom: "",
    notifierLeaveTo: "",
    danger: "",
    info: "",
    success: "",
    warning: "",
    utility: "",
    notifierToastWrapper: "",
    notifierToast: "",
    notifierDangerIcon: "",
    notifierToastHeader: "",
    notifierTitleIconBoxWrapper: "",
    notifierTitleIconBox: "",
    notifierToastClose: "",
    notifierTitleIconDanger: "",
    notifierTitleIconInfo: "",
    notifierTitleIconSuccess: "",
    notifierTitleIconWarning: "",
    notifierTitleIconUtility: "",
    notifierTitleIcon: "",
    notifierTitle: "",
    notifierContent: ""
  },
  offCanvas: {
    container: "",
    offCanvasEnter: "",
    offCanvasEnterFrom: "",
    offCanvasEnterTo: "",
    offCanvasLeave: "",
    offCanvasLeaveFrom: "",
    offCanvasLeaveTo: "",
    offCanvasContent: ""
  },
  personComboboxInput: {
    optionLayout: "",
    optionLabel: "",
    optionName: "",
    optionEmail: ""
  },
  popup: {
    container: "",
    popupButton: ""
  },
  radio: {
    container: ""
  },
  rating: {
    container: "",
    ratingStar: "",
    ratingStarL: "",
    ratingHalfStar: ""
  },
  spinner: {
    container: "",
    spinnerInline: "",
    spinnerBlock: "",
    spinnerBlockInner: ""
  },
  switch: {
    container: "",
    switchButtonWrapper: "",
    switchButtonWrapperReverse: "",
    switchButton: "",
    switchButtonChecked: "",
    switchButtonUnchecked: "",
    switchToggle: "",
    switchToggleChecked: "",
    switchToggleUnchecked: "",
    switchLabel: ""
  },
  tabBar: {
    container: "",
    tabBarTwoOptions: "",
    tabBarSingleOptions: "",
    tabBarTabTwoOptions: "",
    tabBarTabActive: "",
    tabBarButtonDisabled: "",
    tabBarButtonTwoOptions: "",
    tabBarButtonDisabledIcon: "",
    tabBarButton: "",
    tabBarButtonLabelTwoOptions: "",
    tabBarButtonIcon: ""
  },
  table: {
    container: "",
    tableContainerScrollable: "",
    table: "",
    tableScrollable: "",
    tableStriped: ""
  },
  tableCell: {
    container: "",
    cellContainer: ""
  },
  tableHeader: {
    container: "",
    tableHeader: ""
  },
  tableRow: {
    container: ""
  },
  tooltip: {
    container: "",
    tooltipButton: "",
    tooltipButtonIcon: "",
    tooltipButtonSlotDefault: "",
    tooltipButtonSlotDefaultSmallText: "",
    tooltipButtonSlotDefaultLargeText: "",
    tooltip: "",
    tooltipArrow: "",
    tooltipBorder: "",
    tooltipSlot: "",
    tooltipContent: "",
    tooltipBgColorClassComputedLight: "",
    tooltipBgColorClassComputedDark: "",
    tooltipBorderColorClassComputed: "",
    tooltipBorderColorClassComputedDefault: "",
    tooltipTextColorClassComputedLight: "",
    tooltipTextColorClassComputedDark: ""
  },
  dataTable: {
    container: "",
    dataTableScrollableContainer: "",
    dataTableScrollable: "",
    dataTableLoadingWrapper: "",
    dataTableSelectableCell: ""
  }
};

function handleReset(classList) {
  const resetToken = "$reset";
  if (!classList?.includes(resetToken))
    return classList;
  return `$reset ${classList.split(resetToken).at(-1).trim()}`;
}

function handleRemove(classList) {
  const removalToken = "$remove:";
  const removalTokenEscaped = escapeRegex(removalToken);
  while (classList.includes(removalToken)) {
    const classToRemove = classList.split(removalToken)[1]?.split(" ")[0] || "";
    const classToRemoveEscaped = escapeRegex(classToRemove);
    classList = classList.replace(new RegExp(`${removalTokenEscaped}${classToRemoveEscaped} *`), "").replaceAll(new RegExp(`${classToRemoveEscaped} *`, "gi"), "");
  }
  return classList.trim();
}

function handlePrefix(type, sectionKey, classList) {
  return `${type}-${sectionKey} ${classList}`.trim();
}

function formatThemeObject(classes, selectedType) {
  const classesBySectionKey = deepCopy(classes);
  const formattedThemeObject = {};
  Object.keys(classesBySectionKey).forEach((sectionKey) => {
    formattedThemeObject[sectionKey] = compose(
      handleReset,
      handleRemove,
      handlePrefix.bind(null, selectedType, sectionKey)
    )(classesBySectionKey[sectionKey]);
  });
  return formattedThemeObject;
}

function useTheme(type, customThemeClasses, { defaultTheme } = {}) {
  defaultTheme = defaultTheme || globalConfig.theme || defaultLightTheme;
  const mergedTypeClasses = deepConcat([emptyTheme[type], defaultTheme[type], customThemeClasses?.[type]]);
  const componentClasses = Object.freeze(formatThemeObject(mergedTypeClasses, type));
  return {
    componentClasses
  };
}

var ThemeTypes = /* @__PURE__ */ ((ThemeTypes2) => {
  ThemeTypes2["ACCORDION"] = "accordion";
  ThemeTypes2["ACCORDION_FORM_TEMPLATE"] = "accordionFormTemplate";
  ThemeTypes2["ALERT_COMPONENT"] = "alertComponent";
  ThemeTypes2["ASYNC_BUTTON"] = "asyncButton";
  ThemeTypes2["AVATAR"] = "avatar";
  ThemeTypes2["AVATARLIST"] = "avatarList";
  ThemeTypes2["BACKDROP"] = "backdrop";
  ThemeTypes2["BADGE"] = "badge";
  ThemeTypes2["BREADCRUMBS"] = "breadcrumbs";
  ThemeTypes2["BUTTON"] = "button";
  ThemeTypes2["CALENDAR"] = "calendar";
  ThemeTypes2["CALENDAR_INPUT"] = "calendarInput";
  ThemeTypes2["CARD"] = "card";
  ThemeTypes2["CHECKBOX"] = "checkbox";
  ThemeTypes2["CHIP"] = "chip";
  ThemeTypes2["CLIPBOARD"] = "clipboard";
  ThemeTypes2["COLLAPSE"] = "collapse";
  ThemeTypes2["COMBOBOX_INPUT"] = "comboboxInput";
  ThemeTypes2["DATAPAGER"] = "dataPager";
  ThemeTypes2["DATAPAGER_SUMMARY"] = "dataPagerSummary";
  ThemeTypes2["DATA_SORTER"] = "dataSorter";
  ThemeTypes2["DATE_CALENDAR_INPUT"] = "dateCalendarInput";
  ThemeTypes2["DATE_RANGE_INPUT"] = "dateRangeInput";
  ThemeTypes2["DEBUG_INFO"] = "debugInfo";
  ThemeTypes2["DROPDOWN"] = "dropdown";
  ThemeTypes2["ICON"] = "icon";
  ThemeTypes2["LISTBOX_INPUT"] = "listboxInput";
  ThemeTypes2["LOADING_WRAPPER"] = "loadingWrapper";
  ThemeTypes2["MODAL"] = "modal";
  ThemeTypes2["NOTIFIER"] = "notifier";
  ThemeTypes2["NUMBER_INPUT"] = "numberInput";
  ThemeTypes2["OFF_CANVAS"] = "offCanvas";
  ThemeTypes2["PERSON_COMBOBOX_INPUT"] = "personComboboxInput";
  ThemeTypes2["POPUP"] = "popup";
  ThemeTypes2["PROGRESS_BAR"] = "progressBar";
  ThemeTypes2["RADIO"] = "radio";
  ThemeTypes2["RATING"] = "rating";
  ThemeTypes2["SPINNER"] = "spinner";
  ThemeTypes2["STEPPER"] = "stepper";
  ThemeTypes2["SWITCH"] = "switch";
  ThemeTypes2["TAB_BAR"] = "tabBar";
  ThemeTypes2["TABLE"] = "table";
  ThemeTypes2["TABLE_CELL"] = "tableCell";
  ThemeTypes2["TABLE_HEADER"] = "tableHeader";
  ThemeTypes2["TABLE_ROW"] = "tableRow";
  ThemeTypes2["TIME_INPUT"] = "timeInput";
  ThemeTypes2["TOOLTIP"] = "tooltip";
  ThemeTypes2["DATA_TABLE"] = "dataTable";
  return ThemeTypes2;
})(ThemeTypes || {});

const _export_sfc = (sfc, props) => {
  const target = sfc.__vccOpts || sfc;
  for (const [key, val] of props) {
    target[key] = val;
  }
  return target;
};

const _sfc_main$g = /* @__PURE__ */ defineComponent({
  __name: "DSIcon",
  props: {
    icon: { default: "" },
    size: { default: 18 },
    customThemeObject: {}
  },
  setup(__props) {
    const props = __props;
    const theme = computed(() => {
      const { componentClasses } = useTheme(ThemeTypes.ICON, props.customThemeObject);
      return componentClasses;
    });
    const iconContainerSize = computed(() => {
      const sizes = {
        16: {
          classes: theme.value.iconSizeXs
        },
        18: {
          classes: theme.value.iconSizeSm
        },
        24: {
          classes: theme.value.iconSizeMd
        },
        32: {
          classes: theme.value.iconSizeLg
        },
        48: {
          classes: theme.value.iconSizeXl
        }
      };
      return sizes[props.size]?.classes || sizes["16"].classes;
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        "data-ds-component": "ds-icon",
        class: normalizeClass([iconContainerSize.value, theme.value.container]),
        "data-testid": "ds-icon-container"
      }, [
        createElementVNode("i", {
          class: normalizeClass(props.icon),
          "data-testid": "ds-icon-fa-icon"
        }, null, 2)
      ], 2);
    };
  }
});

const _sfc_main$f = /* @__PURE__ */ defineComponent({
  ...{
    inheritAttrs: false
  },
  __name: "DSPopup",
  props: {
    position: {},
    shift: { type: [Boolean, Number, Object], default: true },
    offset: { default: 4 },
    placement: { default: "bottom-start" },
    matchWidth: { type: Boolean },
    flip: { type: [Boolean, Number, Object] },
    useShow: { type: Boolean },
    middleware: {},
    customThemeObject: {}
  },
  emits: ["open", "close"],
  setup(__props, { expose: __expose, emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const isOpen = ref(false);
    const positionStyles = computed(() => {
      if (!props.position)
        return [];
      return Object.entries(props.position).map(([orientation, value]) => `--popup-${orientation}: ${value};`);
    });
    const theme = computed(() => {
      const { componentClasses } = useTheme(ThemeTypes.POPUP, props.customThemeObject);
      return componentClasses;
    });
    watch(isOpen, (newVal) => {
      if (newVal)
        emit("open");
      else
        emit("close");
    });
    function toggleShow() {
      isOpen.value = !isOpen.value;
    }
    function open() {
      isOpen.value = true;
    }
    function close() {
      isOpen.value = false;
    }
    __expose({
      open,
      close
    });
    return (_ctx, _cache) => {
      return openBlock(), createBlock(unref(OnClickOutside), { onTrigger: close }, {
        default: withCtx(() => [
          createVNode(unref(UseElementBounding), null, {
            default: withCtx(({ width }) => [
              createElementVNode("div", mergeProps({
                "data-ds-component": "popup",
                class: { manual: _ctx.position, [theme.value.container]: true },
                style: positionStyles.value.join("")
              }, _ctx.$attrs), [
                createVNode(unref(Float), {
                  shift: _ctx.shift,
                  flip: _ctx.flip,
                  placement: _ctx.placement,
                  show: _ctx.useShow || isOpen.value,
                  offset: _ctx.offset,
                  "vue-transition": "",
                  "transition-type": "transition",
                  middleware: _ctx.middleware
                }, {
                  default: withCtx(() => [
                    createElementVNode("div", {
                      class: normalizeClass(theme.value.popupButton),
                      onClick: _cache[0] || (_cache[0] = withModifiers(($event) => toggleShow(), ["stop", "prevent"])),
                      onKeydown: [
                        _cache[1] || (_cache[1] = withKeys(withModifiers(($event) => toggleShow(), ["stop"]), ["enter"])),
                        _cache[2] || (_cache[2] = withKeys(withModifiers(($event) => toggleShow(), ["stop"]), ["space"]))
                      ]
                    }, [
                      renderSlot(_ctx.$slots, "default", {}, void 0, true)
                    ], 34),
                    withDirectives(createElementVNode("div", {
                      style: normalizeStyle(_ctx.matchWidth && `width: ${width}px !important;`),
                      onKeydown: _cache[3] || (_cache[3] = withKeys(withModifiers(($event) => close(), ["stop"]), ["escape"]))
                    }, [
                      renderSlot(_ctx.$slots, "content", normalizeProps(guardReactiveProps({ isOpen: isOpen.value, close })), void 0, true)
                    ], 36), [
                      [vShow, isOpen.value]
                    ])
                  ]),
                  _: 2
                }, 1032, ["shift", "flip", "placement", "show", "offset", "middleware"])
              ], 16)
            ]),
            _: 3
          })
        ]),
        _: 3
      });
    };
  }
});

const DSPopup = /* @__PURE__ */ _export_sfc(_sfc_main$f, [["__scopeId", "data-v-f1dd9d2a"]]);

const _hoisted_1$6 = ["aria-label"];
const _hoisted_2$2 = ["innerHTML"];
const _sfc_main$e = /* @__PURE__ */ defineComponent({
  __name: "DSTooltip",
  props: {
    content: {},
    open: { type: Boolean },
    iconClass: { default: "i-far-circle-info" },
    iconSize: {},
    ignoreHover: { type: Boolean },
    ignoreFocus: { type: Boolean },
    variant: { default: "dark" },
    arrowless: { type: Boolean },
    bgColorClass: {},
    borderColorClass: {},
    textColorClass: {},
    otherClasses: {},
    portal: { type: Boolean },
    placement: { default: "top" },
    customThemeObject: {},
    disabled: { type: Boolean }
  },
  emits: ["click", "show", "hide"],
  setup(__props, { expose: __expose, emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const isFocused = ref(false);
    const isHovered = ref(false);
    const shouldShow = computed(
      () => !props.disabled && (isFocused.value || isHovered.value || props.open)
    );
    watch(shouldShow, (newVal, oldVal) => {
      if (newVal && !oldVal)
        emit("show");
      else if (!newVal && oldVal)
        emit("hide");
    });
    const theme = computed(() => {
      const { componentClasses } = useTheme(
        ThemeTypes.TOOLTIP,
        props.customThemeObject
      );
      return componentClasses;
    });
    const bgColorClassComputed = computed(() => {
      if (props.bgColorClass)
        return props.bgColorClass;
      switch (props.variant) {
        case "light":
        case "light-bordered":
          return theme.value.tooltipBgColorClassComputedLight;
        case "dark":
        case "dark-bordered":
          return theme.value.tooltipBgColorClassComputedDark;
      }
      return "";
    });
    const borderColorClassComputed = computed(() => {
      if (props.borderColorClass)
        return props.borderColorClass;
      switch (props.variant) {
        case "light-bordered":
        case "dark-bordered":
          return theme.value.tooltipBorderColorClassComputed;
      }
      return theme.value.tooltipBorderColorClassComputedDefault;
    });
    const textColorClassComputed = computed(() => {
      if (props.textColorClass)
        return props.textColorClass;
      switch (props.variant) {
        case "light":
        case "light-bordered":
          return theme.value.tooltipTextColorClassComputedLight;
        case "dark":
        case "dark-bordered":
          return theme.value.tooltipTextColorClassComputedDark;
      }
      return "";
    });
    function toggleClick() {
      emit("click");
    }
    function onHover() {
      if (!props.ignoreHover)
        isHovered.value = true;
    }
    function onUnhover() {
      if (!props.ignoreHover)
        isHovered.value = false;
    }
    function onFocus() {
      if (!props.ignoreFocus)
        isFocused.value = true;
    }
    function onBlur() {
      isFocused.value = false;
    }
    function show() {
      isFocused.value = true;
    }
    function hide() {
      isFocused.value = false;
      isHovered.value = false;
    }
    const { t } = useI18n();
    __expose({
      show,
      hide
    });
    return (_ctx, _cache) => {
      return _ctx.content || _ctx.$slots.content ? (openBlock(), createBlock(unref(OnClickOutside), {
        key: 0,
        onTrigger: hide
      }, {
        default: withCtx(() => [
          createVNode(unref(UseElementBounding), null, {
            default: withCtx(({ width }) => [
              createElementVNode("div", {
                "data-ds-component": "tooltip",
                class: normalizeClass(theme.value.container)
              }, [
                createVNode(unref(Float), mergeProps({
                  shift: "",
                  flip: "",
                  arrow: "",
                  placement: props.placement,
                  offset: 6,
                  show: shouldShow.value
                }, _ctx.$attrs, { portal: _ctx.portal }), {
                  default: withCtx(() => [
                    createElementVNode("button", {
                      "aria-label": unref(t)("Toggle tooltip"),
                      class: normalizeClass(theme.value.tooltipButton),
                      "data-testid": "ds-tooltip-trigger",
                      onClick: withModifiers(toggleClick, ["prevent", "stop"]),
                      onMouseover: onHover,
                      onMouseout: onUnhover,
                      onFocus,
                      onBlur
                    }, [
                      renderSlot(_ctx.$slots, "default", {}, () => [
                        createVNode(_sfc_main$g, {
                          class: normalizeClass(theme.value.tooltipButtonIcon),
                          icon: props.iconClass,
                          size: width === 24 ? 24 : props.iconSize
                        }, null, 8, ["class", "icon", "size"])
                      ])
                    ], 42, _hoisted_1$6),
                    createElementVNode("div", {
                      class: normalizeClass([
                        bgColorClassComputed.value,
                        borderColorClassComputed.value,
                        borderColorClassComputed.value ? theme.value.tooltipBorder : "",
                        textColorClassComputed.value,
                        _ctx.otherClasses,
                        theme.value.tooltip
                      ])
                    }, [
                      !_ctx.arrowless ? (openBlock(), createBlock(unref(FloatArrow), {
                        key: 0,
                        class: normalizeClass([
                          bgColorClassComputed.value,
                          borderColorClassComputed.value ? theme.value.tooltipBorder : "",
                          borderColorClassComputed.value,
                          theme.value.tooltipArrow
                        ])
                      }, null, 8, ["class"])) : createCommentVNode("", true),
                      createElementVNode("div", {
                        class: normalizeClass(theme.value.tooltipSlot)
                      }, [
                        renderSlot(_ctx.$slots, "content", {}, () => [
                          createElementVNode("div", {
                            class: normalizeClass([bgColorClassComputed.value, theme.value.tooltipContent]),
                            "data-testid": "ds-tooltip-content"
                          }, [
                            createElementVNode("div", { innerHTML: _ctx.content }, null, 8, _hoisted_2$2)
                          ], 2)
                        ])
                      ], 2)
                    ], 2)
                  ]),
                  _: 2
                }, 1040, ["placement", "show", "portal"])
              ], 2)
            ]),
            _: 3
          })
        ]),
        _: 3
      })) : renderSlot(_ctx.$slots, "default", { key: 1 });
    };
  }
});

function block0$4 (Component) {
  const _Component = Component;
  _Component.__i18n = _Component.__i18n || [];
  _Component.__i18n.push({
    "locale": "",
    "resource": {
      "en-us": {
        "Toggle tooltip": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Toggle tooltip"}}
      },
      "de": {
        "Toggle tooltip": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Tooltip ein-/ausschalten"}}
      },
      "fr": {
        "Toggle tooltip": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Activer/Désactiver l\u0027infobulle"}}
      },
      "es": {
        "Toggle tooltip": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Activar/Desactivar tooltip"}}
      }
    }
  });
}

if (typeof block0$4 === "function") block0$4(_sfc_main$e);

const _sfc_main$d = /* @__PURE__ */ defineComponent({
  __name: "DSButton",
  props: {
    as: { default: "button" },
    href: {},
    customThemeObject: {}
  },
  setup(__props) {
    const props = __props;
    const focus = ref(false);
    const hover = ref(false);
    const pressed = ref(false);
    const theme = computed(() => {
      const { componentClasses } = useTheme(ThemeTypes.BUTTON, props.customThemeObject);
      return componentClasses;
    });
    return (_ctx, _cache) => {
      return openBlock(), createBlock(resolveDynamicComponent(props.as), {
        type: props.as === "button" ? "button" : null,
        href: props.as === "a" ? props.href : null,
        "data-ds-component": "button",
        "data-testid": "ds-button-trigger",
        class: normalizeClass(theme.value.container),
        onFocus: _cache[0] || (_cache[0] = ($event) => focus.value = true),
        onBlur: _cache[1] || (_cache[1] = ($event) => focus.value = false),
        onMouseenter: _cache[2] || (_cache[2] = ($event) => hover.value = true),
        onMouseleave: _cache[3] || (_cache[3] = ($event) => {
          hover.value = false;
          pressed.value = false;
        }),
        onMousedown: _cache[4] || (_cache[4] = ($event) => pressed.value = true),
        onMouseup: _cache[5] || (_cache[5] = ($event) => pressed.value = false)
      }, {
        default: withCtx(() => [
          renderSlot(_ctx.$slots, "default", normalizeProps(guardReactiveProps({ focus: focus.value, hover: hover.value, pressed: pressed.value })))
        ]),
        _: 3
      }, 40, ["type", "href", "class"]);
    };
  }
});

const _sfc_main$c = {
  name: 'CalendarDay',
  props: {
    date: String,
    selected: Boolean,
    underlined: Boolean,
    disabled: Boolean,
    customThemeObject: {
      type: Object,
      required: false,
    },
  },
  setup(props) {
    const dayjs = useDayjs();
    const theme = computed(() => {
      const { componentClasses } = useTheme(ThemeTypes.CALENDAR, props.customThemeObject);
      return componentClasses
    });
    return { dayjs, theme }
  },
  computed: {
    today() {
      return this.dayjs(this.date).isToday()
    },
  },
};

function _sfc_render$5(_ctx, _cache, $props, $setup, $data, $options) {
  return (openBlock(), createElementBlock("button", {
    class: normalizeClass([
      $setup.theme.calendarDayButton,
      { today: $options.today, selected: $props.selected && !$props.disabled, disabled: $props.disabled },
    ])
  }, [
    createElementVNode("span", {
      class: normalizeClass([
        $props.underlined && $setup.theme.calendarDateUnderLined,
        !$props.underlined ? '' : $props.selected && !$props.disabled
          ? $setup.theme.calendarDateNotUnderlinedSelected
          : $setup.theme.calendarDateNotUnderlined,
      ])
    }, toDisplayString($setup.dayjs($props.date).date()), 3)
  ], 2))
}
const CalendarDay = /*#__PURE__*/_export_sfc(_sfc_main$c, [['render',_sfc_render$5],['__scopeId',"data-v-25190c9b"]]);

const _sfc_main$b = {
  name: 'DSCalendar',
  components: { CalendarDay },
  props: {
    selected: Array,
    active: Array,
    underlined: Array,
    mode: String,
    hoverMode: String,
    month: String,
    customFirstDayOfWeek: Number,
    disablePastDates: Boolean,
    customThemeObject: {
      type: Object,
      required: false,
    },
  },
  emits: [
    'updateMonth',
    'toggle',
    'hover',
    'mouseDown',
  ],
  setup(props) {
    const dayjs = useDayjs();
    const { getWeekView, getMonthView, withConfig } = useCalendar();
    const theme = computed(() => {
      const { componentClasses } = useTheme(ThemeTypes.CALENDAR, props.customThemeObject);
      return componentClasses
    });
    return { dayjs, getWeekView, getMonthView, withConfig, theme }
  },
  computed: {
    date() {
      return this.dayjs(this.calendarMonth).format('YYYY-MM-DD')
    },
    weekdays() {
      return this.getWeekView(this.date).map(day => day.format('ddd'))
    },
    dates() {
      return this.getMonthView(this.date).map(day => day.format('YYYY-MM-DD'))
    },
    numWeeks() {
      return this.dates?.length / 7
    },
    calendarMonth: {
      get() {
        return this.month
      },
      set(value) {
        this.$emit('updateMonth', value);
      },
    },
  },
  watch: {
    customFirstDayOfWeek: {
      handler(value) {
        if (value !== undefined)
          this.withConfig({ customFirstDayOfWeek: value });
      },
      immediate: true,
    },
  },
  methods: {
    onDayClick(date) {
      const dayObject = this.dayjs(date);
      const calendarMonthObject = this.dayjs(this.calendarMonth);
      if (dayObject.month() === calendarMonthObject.month() || this.mode === 'range')
        this.$emit('toggle', date);
      else
        this.calendarMonth = dayObject.format('YYYY-MM');
    },
    onDayHover(date) {
      if (this.hoverMode)
        this.$emit('hover', date);
    },
    getDateByIndex(w, d) {
      return this.dates[((w - 1) * 7 + (d - 1))]
    },
    getDayClasses(date) {
      return {
        disabled: this.dayjs(date).month() !== this.dayjs(this.calendarMonth).month(),
        selected: this.selected?.includes(date),
        active: this.active?.includes(date),
      }
    },
    isDayDisabled(date) {
      const isInDifferentMonth = this.dayjs(date).month() !== this.dayjs(this.calendarMonth).month();
      const isDisabledPastDate = this.disablePastDates && this.dayjs(date).isBefore(this.dayjs());

      return isInDifferentMonth || isDisabledPastDate
    },
  },
};

function _sfc_render$4(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_CalendarDay = resolveComponent("CalendarDay");

  return (openBlock(), createElementBlock("div", {
    class: normalizeClass($setup.theme.container),
    "data-ds-component": "calendar"
  }, [
    createElementVNode("div", {
      class: normalizeClass($setup.theme.calendarContent)
    }, [
      createElementVNode("div", {
        class: normalizeClass($setup.theme.calendarWeekdays)
      }, [
        (openBlock(true), createElementBlock(Fragment, null, renderList($options.weekdays, (day) => {
          return (openBlock(), createElementBlock("div", {
            key: day,
            class: normalizeClass($setup.theme.calendarWeekdayText),
            tabindex: "0"
          }, toDisplayString(day), 3))
        }), 128))
      ], 2),
      (openBlock(true), createElementBlock(Fragment, null, renderList($options.numWeeks, (w) => {
        return (openBlock(), createElementBlock("div", {
          key: w,
          class: normalizeClass($setup.theme.calendarWeeks)
        }, [
          (openBlock(), createElementBlock(Fragment, null, renderList(7, (d) => {
            return createVNode(_component_CalendarDay, {
              key: d,
              date: $options.getDateByIndex(w, d),
              class: normalizeClass([$options.getDayClasses($options.getDateByIndex(w, d)), $setup.theme.calendarDay]),
              underlined: $props.underlined?.includes($options.getDateByIndex(w, d)),
              selected: $props.selected?.includes($options.getDateByIndex(w, d)),
              disabled: $options.isDayDisabled($options.getDateByIndex(w, d)),
              "custom-theme-object": $props.customThemeObject,
              onClick: withModifiers($event => ($options.onDayClick($options.getDateByIndex(w, d))), ["prevent"]),
              onMousedown: $event => (_ctx.$emit('mouseDown', $options.getDateByIndex(w, d))),
              onMouseover: $event => ($options.onDayHover($options.getDateByIndex(w, d)))
            }, null, 8, ["date", "class", "underlined", "selected", "disabled", "custom-theme-object", "onClick", "onMousedown", "onMouseover"])
          }), 64))
        ], 2))
      }), 128))
    ], 2)
  ], 2))
}
const DSCalendar = /*#__PURE__*/_export_sfc(_sfc_main$b, [['render',_sfc_render$4]]);

function match(value, lookup, ...args) {
  if (value in lookup) {
    const returnValue = lookup[value];
    return typeof returnValue === "function" ? returnValue(...args) : returnValue;
  }
  const error = new Error(
    `Tried to handle "${value}" but there is no handler defined. Only defined handlers are: ${Object.keys(
      lookup
    ).map((key) => `"${key}"`).join(", ")}.`
  );
  if (Error.captureStackTrace)
    Error.captureStackTrace(error, match);
  throw error;
}

var Features = /* @__PURE__ */ ((Features2) => {
  Features2[Features2["None"] = 0] = "None";
  Features2[Features2["RenderStrategy"] = 1] = "RenderStrategy";
  Features2[Features2["Static"] = 2] = "Static";
  return Features2;
})(Features || {});
function render({
  visible = true,
  features = 0 /* None */,
  ...main
}) {
  if (visible)
    return _render(main);
  if (features & 2 /* Static */) {
    if (main.props.static)
      return _render(main);
  }
  if (features & 1 /* RenderStrategy */) {
    const strategy = main.props.unmount ?? true ? 0 /* Unmount */ : 1 /* Hidden */;
    return match(strategy, {
      [0 /* Unmount */]() {
        return null;
      },
      [1 /* Hidden */]() {
        return _render({
          ...main,
          props: { ...main?.props ?? {}, hidden: true, style: { display: "none" } }
        });
      }
    });
  }
  return _render(main);
}
function _render({
  props,
  attrs,
  slots,
  slot
}) {
  const { as, ...passThroughProps } = omit(props, ["unmount", "static"]);
  const children = slots.default?.(slot);
  if (as === "template") {
    if (Object.keys(passThroughProps).length > 0 || "class" in attrs) {
      const [firstChild, ...other] = children ?? [];
      if (other.length > 0)
        throw new Error('You should only render 1 child or use the `as="..."` prop');
      return cloneVNode(firstChild, passThroughProps);
    }
    return children;
  }
  return h(as, passThroughProps, children);
}
function omit(object, keysToOmit = []) {
  const clone = Object.assign({}, object);
  for (const key of keysToOmit) {
    if (key in clone)
      delete clone[key];
  }
  return clone;
}

var Keys = /* @__PURE__ */ ((Keys2) => {
  Keys2["Space"] = " ";
  Keys2["Enter"] = "Enter";
  Keys2["Escape"] = "Escape";
  Keys2["Backspace"] = "Backspace";
  Keys2["ArrowUp"] = "ArrowUp";
  Keys2["ArrowDown"] = "ArrowDown";
  Keys2["ArrowLeft"] = "ArrowLeft";
  Keys2["ArrowRight"] = "ArrowRight";
  Keys2["Home"] = "Home";
  Keys2["End"] = "End";
  Keys2["PageUp"] = "PageUp";
  Keys2["PageDown"] = "PageDown";
  Keys2["Tab"] = "Tab";
  return Keys2;
})(Keys || {});

function assertNever(x) {
  throw new Error(`Unexpected object: ${x}`);
}
var Focus = /* @__PURE__ */ ((Focus2) => {
  Focus2[Focus2["First"] = 0] = "First";
  Focus2[Focus2["Previous"] = 1] = "Previous";
  Focus2[Focus2["Next"] = 2] = "Next";
  Focus2[Focus2["Last"] = 3] = "Last";
  Focus2[Focus2["Specific"] = 4] = "Specific";
  Focus2[Focus2["Nothing"] = 5] = "Nothing";
  return Focus2;
})(Focus || {});
function calculateActiveIndex(action, resolvers) {
  const items = resolvers.resolveItems();
  if (items.length <= 0)
    return null;
  const currentActiveIndex = resolvers.resolveActiveIndex();
  const activeIndex = currentActiveIndex ?? -1;
  const nextActiveIndex = (() => {
    switch (action.focus) {
      case 0 /* First */:
        return items.findIndex((item) => !resolvers.resolveDisabled(item));
      case 1 /* Previous */: {
        const idx = items.slice().reverse().findIndex((item, idx2, all) => {
          if (activeIndex !== -1 && all.length - idx2 - 1 >= activeIndex)
            return false;
          return !resolvers.resolveDisabled(item);
        });
        if (idx === -1)
          return idx;
        return items.length - 1 - idx;
      }
      case 2 /* Next */:
        return items.findIndex((item, idx) => {
          if (idx <= activeIndex)
            return false;
          return !resolvers.resolveDisabled(item);
        });
      case 3 /* Last */: {
        const idx = items.slice().reverse().findIndex((item) => !resolvers.resolveDisabled(item));
        if (idx === -1)
          return idx;
        return items.length - 1 - idx;
      }
      case 4 /* Specific */:
        return items.findIndex((item) => resolvers.resolveId(item) === action.id);
      case 5 /* Nothing */:
        return null;
      default:
        assertNever(action);
    }
  })();
  return nextActiveIndex === -1 ? currentActiveIndex : nextActiveIndex;
}

function resolvePropValue(property, bag) {
  if (property === void 0)
    return void 0;
  if (typeof property === "function")
    return property(bag);
  return property;
}

function nextFrame(cb) {
  requestAnimationFrame(() => requestAnimationFrame(cb));
}
const ListboxContext = Symbol("ListboxContext");
function useListboxContext(component) {
  const context = inject(ListboxContext, null);
  if (context === null) {
    const err = new Error(
      `<${component} /> is missing a parent <Listbox /> component.`
    );
    if (Error.captureStackTrace)
      Error.captureStackTrace(err, useListboxContext);
    throw err;
  }
  return context;
}
const Listbox = defineComponent({
  name: "Listbox",
  props: {
    as: { type: [Object, String], default: "template" },
    disabled: { type: [Boolean], default: false },
    modelValue: {
      type: [Object, String, Number, Boolean, Array],
      default: null
    },
    multiselect: { type: [Boolean], default: false },
    deselect: { type: [Boolean], default: true }
  },
  emits: ["update:modelValue"],
  setup(props, { slots, attrs, emit }) {
    const { modelValue, disabled, ...passThroughProps } = props;
    const listboxState = ref(
      1 /* Closed */
    );
    const labelRef = ref(null);
    const buttonRef = ref(null);
    const optionsRef = ref(null);
    const options = ref([]);
    const searchQuery = ref("");
    const activeOptionIndex = ref(null);
    const multiple = props.multiselect;
    const deselect = props.deselect;
    const value = computed(() => props.modelValue);
    const api = {
      listboxState,
      value,
      labelRef,
      buttonRef,
      optionsRef,
      disabled,
      options,
      searchQuery,
      activeOptionIndex,
      multiple,
      closeListbox(force = false) {
        if (disabled)
          return;
        if (multiple && force === false)
          return;
        if (listboxState.value === 1 /* Closed */)
          return;
        listboxState.value = 1 /* Closed */;
        activeOptionIndex.value = null;
      },
      openListbox() {
        if (disabled)
          return;
        if (listboxState.value === 0 /* Open */)
          return;
        listboxState.value = 0 /* Open */;
      },
      goToOption(focus, id) {
        if (disabled)
          return;
        if (listboxState.value === 1 /* Closed */)
          return;
        const nextActiveOptionIndex = calculateActiveIndex(
          focus === Focus.Specific ? { focus: Focus.Specific, id } : { focus },
          {
            resolveItems: () => options.value,
            resolveActiveIndex: () => activeOptionIndex.value,
            resolveId: (option) => option.id,
            resolveDisabled: (option) => option.dataRef.disabled
          }
        );
        if (searchQuery.value === "" && activeOptionIndex.value === nextActiveOptionIndex)
          return;
        searchQuery.value = "";
        activeOptionIndex.value = nextActiveOptionIndex;
      },
      search(value2) {
        if (disabled)
          return;
        if (listboxState.value === 1 /* Closed */)
          return;
        searchQuery.value += value2;
        const match = options.value.findIndex(
          (option) => !option.dataRef.disabled && option.dataRef.textValue.startsWith(searchQuery.value)
        );
        if (match === -1 || match === activeOptionIndex.value)
          return;
        activeOptionIndex.value = match;
      },
      clearSearch() {
        if (disabled)
          return;
        if (listboxState.value === 1 /* Closed */)
          return;
        if (searchQuery.value === "")
          return;
        searchQuery.value = "";
      },
      registerOption(id, dataRef) {
        options.value.push({ id, dataRef });
      },
      unregisterOption(id) {
        const nextOptions = options.value.slice();
        const currentActiveOption = activeOptionIndex.value !== null ? nextOptions[activeOptionIndex.value] : null;
        const idx = nextOptions.findIndex((a) => a.id === id);
        if (idx !== -1)
          nextOptions.splice(idx, 1);
        options.value = nextOptions;
        activeOptionIndex.value = (() => {
          if (idx === activeOptionIndex.value)
            return null;
          if (currentActiveOption === null)
            return null;
          return nextOptions.indexOf(currentActiveOption);
        })();
      },
      select(value2) {
        if (disabled)
          return;
        if (multiple) {
          const arr = toRaw(props?.modelValue) ?? [];
          const index = arr.indexOf(value2);
          if (index === -1) {
            emit("update:modelValue", [...arr, value2]);
          } else {
            emit(
              "update:modelValue",
              arr.filter((x, i) => i !== index)
            );
          }
        } else {
          if (props?.modelValue === value2) {
            if (deselect)
              emit("update:modelValue", void 0);
          } else {
            emit("update:modelValue", value2);
          }
        }
      }
    };
    onMounted(() => {
      function handler(event) {
        const target = event.target;
        const active = document.activeElement;
        if (listboxState.value !== 0 /* Open */)
          return;
        if (buttonRef.value?.contains(target))
          return;
        if (!optionsRef.value?.contains(target))
          api.closeListbox(true);
        if (active !== document.body && active?.contains(target))
          return;
        if (!event.defaultPrevented)
          buttonRef.value?.focus({ preventScroll: true });
      }
      window.addEventListener("mousedown", handler);
      onUnmounted(() => window.removeEventListener("mousedown", handler));
    });
    provide(ListboxContext, api);
    return () => {
      const slot = { open: listboxState.value === 0 /* Open */, disabled };
      return render({ props: passThroughProps, slot, slots, attrs });
    };
  }
});
defineComponent({
  name: "ListboxLabel",
  props: { as: { type: [Object, String], default: "label" } },
  setup() {
    const api = useListboxContext("ListboxLabel");
    const id = `headlessui-listbox-label-${useId()}`;
    return {
      id,
      el: api.labelRef,
      handleClick() {
        api.buttonRef.value?.focus({ preventScroll: true });
      }
    };
  },
  render() {
    const api = useListboxContext("ListboxLabel");
    const slot = {
      open: api.listboxState.value === 0 /* Open */,
      disabled: api.disabled
    };
    const propsWeControl = { id: this.id, ref: "el", onClick: this.handleClick };
    return render({
      props: { ...this.$props, ...propsWeControl },
      slot,
      attrs: this.$attrs,
      slots: this.$slots
    });
  }
});
const ListboxButton = defineComponent({
  name: "ListboxButton",
  props: {
    as: { type: [Object, String], default: "button" }
  },
  setup() {
    const api = useListboxContext("ListboxButton");
    const id = `headlessui-listbox-button-${useId()}`;
    function handleKeyDown(event) {
      switch (event.key) {
        case Keys.Space:
        case Keys.Enter:
        case Keys.ArrowDown:
          event.preventDefault();
          api.openListbox();
          nextTick(() => {
            api.optionsRef.value?.focus({ preventScroll: true });
            if (!api.value.value)
              api.goToOption(Focus.First);
          });
          break;
        case Keys.ArrowUp:
          event.preventDefault();
          api.openListbox();
          nextTick(() => {
            api.optionsRef.value?.focus({ preventScroll: true });
            if (!api.value.value)
              api.goToOption(Focus.Last);
          });
          break;
      }
    }
    function handleClick(event) {
      if (api.disabled)
        return;
      if (api.listboxState.value === 0 /* Open */) {
        api.closeListbox(true);
        nextTick(() => api.buttonRef.value?.focus({ preventScroll: true }));
      } else {
        event.preventDefault();
        api.openListbox();
        nextFrame(() => api.optionsRef.value?.focus({ preventScroll: true }));
      }
    }
    return { id, el: api.buttonRef, handleKeyDown, handleClick };
  },
  render() {
    const api = useListboxContext("ListboxButton");
    const slot = {
      open: api.listboxState.value === 0 /* Open */,
      disabled: api.disabled
    };
    const propsWeControl = {
      "ref": "el",
      "id": this.id,
      "type": "button",
      "aria-haspopup": true,
      "aria-controls": api.optionsRef.value?.id,
      "aria-expanded": api.listboxState.value === 0 /* Open */ ? true : void 0,
      "aria-labelledby": api.labelRef.value ? [api.labelRef.value.id, this.id].join(" ") : void 0,
      "disabled": api.disabled,
      "onKeydown": this.handleKeyDown,
      "onClick": this.handleClick
    };
    return render({
      props: { ...this.$props, ...propsWeControl },
      slot,
      attrs: this.$attrs,
      slots: this.$slots
    });
  }
});
const ListboxOptions = defineComponent({
  name: "ListboxOptions",
  props: {
    as: { type: [Object, String], default: "ul" },
    static: { type: Boolean, default: false },
    unmount: { type: Boolean, default: true }
  },
  setup() {
    const api = useListboxContext("ListboxOptions");
    const id = `headlessui-listbox-options-${useId()}`;
    const searchDebounce = ref(null);
    function handleKeyDown(event) {
      if (searchDebounce.value)
        clearTimeout(searchDebounce.value);
      switch (event.key) {
        case Keys.Space:
          if (api.searchQuery.value !== "") {
            event.preventDefault();
            return api.search(event.key);
          }
        case Keys.Enter:
          event.preventDefault();
          if (api.activeOptionIndex.value !== null) {
            const { dataRef } = api.options.value[api.activeOptionIndex.value];
            api.select(dataRef.value);
          }
          api.closeListbox(false);
          nextTick(() => api.buttonRef.value?.focus({ preventScroll: true }));
          break;
        case Keys.ArrowDown:
          event.preventDefault();
          return api.goToOption(Focus.Next);
        case Keys.ArrowUp:
          event.preventDefault();
          return api.goToOption(Focus.Previous);
        case Keys.Home:
        case Keys.PageUp:
          event.preventDefault();
          return api.goToOption(Focus.First);
        case Keys.End:
        case Keys.PageDown:
          event.preventDefault();
          return api.goToOption(Focus.Last);
        case Keys.Escape:
          event.preventDefault();
          api.closeListbox(true);
          nextTick(() => api.buttonRef.value?.focus({ preventScroll: true }));
          break;
        case Keys.Tab:
          return event.preventDefault();
        default:
          if (event.key.length === 1) {
            api.search(event.key);
            searchDebounce.value = setTimeout(() => api.clearSearch(), 350);
          }
          break;
      }
    }
    return { id, el: api.optionsRef, handleKeyDown };
  },
  render() {
    const api = useListboxContext("ListboxOptions");
    const slot = { open: api.listboxState.value === 0 /* Open */ };
    const propsWeControl = {
      "aria-activedescendant": api.activeOptionIndex.value === null ? void 0 : api.options.value[api.activeOptionIndex.value]?.id,
      "aria-labelledby": api.labelRef.value?.id ?? api.buttonRef.value?.id,
      "id": this.id,
      "onKeydown": this.handleKeyDown,
      "role": "listbox",
      "tabIndex": 0,
      "ref": "el"
    };
    const passThroughProps = this.$props;
    return render({
      props: { ...passThroughProps, ...propsWeControl },
      slot,
      attrs: this.$attrs,
      slots: this.$slots,
      features: Features.RenderStrategy | Features.Static,
      visible: slot.open
    });
  }
});
const ListboxOption = defineComponent({
  name: "ListboxOption",
  props: {
    as: { type: [Object, String], default: "li" },
    value: { type: [Object, String, Number], default: null },
    disabled: { type: Boolean, default: false },
    class: { type: [String, Function], required: false },
    className: { type: [String, Function], required: false }
  },
  setup(props, { slots, attrs }) {
    const api = useListboxContext("ListboxOption");
    const id = `headlessui-listbox-option-${useId()}`;
    const {
      disabled,
      class: defaultClass,
      className = defaultClass,
      value
    } = props;
    const active = computed(() => {
      return api.activeOptionIndex.value !== null ? api.options.value[api.activeOptionIndex.value].id === id : false;
    });
    const selected = computed(() => {
      const rawApiValue = toRaw(api.value.value);
      const rawValue = toRaw(value);
      if (api.multiple)
        return rawApiValue.includes(rawValue);
      else
        return rawApiValue === rawValue;
    });
    const dataRef = ref({
      disabled,
      value,
      textValue: ""
    });
    onMounted(() => {
      const textValue = document.getElementById(id)?.textContent?.toLowerCase().trim();
      if (textValue !== void 0)
        dataRef.value.textValue = textValue;
    });
    onMounted(() => api.registerOption(id, dataRef));
    onUnmounted(() => api.unregisterOption(id));
    onMounted(() => {
      watch(
        [api.listboxState, selected],
        () => {
          if (api.listboxState.value !== 0 /* Open */)
            return;
          if (!selected.value)
            return;
          if (api.multiple)
            return;
          api.goToOption(Focus.Specific, id);
          document.getElementById(id)?.focus?.();
        },
        { immediate: true }
      );
    });
    watchEffect(() => {
      if (api.listboxState.value !== 0 /* Open */)
        return;
      if (!active.value)
        return;
      nextTick(
        () => document.getElementById(id)?.scrollIntoView?.({ block: "nearest" })
      );
    });
    function handleClick(event) {
      if (disabled)
        return event.preventDefault();
      api.select(value);
      api.closeListbox(false);
      nextTick(() => api.buttonRef.value?.focus({ preventScroll: true }));
    }
    function handleFocus() {
      if (disabled)
        return api.goToOption(Focus.Nothing);
      api.goToOption(Focus.Specific, id);
    }
    function handleMove() {
      if (disabled)
        return;
      if (active.value)
        return;
      api.goToOption(Focus.Specific, id);
    }
    function handleLeave() {
      if (disabled)
        return;
      if (!active.value)
        return;
      api.goToOption(Focus.Nothing);
    }
    return () => {
      const slot = { active: active.value, selected: selected.value, disabled };
      const propsWeControl = {
        id,
        "role": "option",
        "tabIndex": -1,
        "class": resolvePropValue(className, slot),
        "aria-disabled": disabled === true ? true : void 0,
        "aria-selected": selected.value === true ? selected.value : void 0,
        "onClick": handleClick,
        "onFocus": handleFocus,
        "onPointermove": handleMove,
        "onMousemove": handleMove,
        "onPointerleave": handleLeave,
        "onMouseleave": handleLeave
      };
      return render({
        props: { ...props, ...propsWeControl },
        slot,
        attrs,
        slots
      });
    };
  }
});

function block0$3 (Component) {
  const _Component = Component;
  _Component.__i18n = _Component.__i18n || [];
  _Component.__i18n.push({
    "locale": "",
    "resource": {
      "en-us": {
        "Select an option": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Select an option"}},
        "Dropdown updated with value: {option}": {"t":0,"b":{"t":2,"i":[{"t":3,"v":"Dropdown updated with value: "},{"t":4,"k":"option"}]}}
      }
    }
  });
}

function block1 (Component) {
  const _Component = Component;
  _Component.__i18n = _Component.__i18n || [];
  _Component.__i18n.push({
    "locale": "de",
    "resource": {
      "Select an option": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Eine Option wählen"}}
    }
  });
}

function block2 (Component) {
  const _Component = Component;
  _Component.__i18n = _Component.__i18n || [];
  _Component.__i18n.push({
    "locale": "es",
    "resource": {
      "Select an option": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Selecciona una opción"}}
    }
  });
}

function block3 (Component) {
  const _Component = Component;
  _Component.__i18n = _Component.__i18n || [];
  _Component.__i18n.push({
    "locale": "fr",
    "resource": {
      "Select an option": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Sélectionner une option"}}
    }
  });
}

const _sfc_main$a = {
  name: 'DSListboxInput',
  components: {
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    DSPopup,
  },
  props: {
    /** The external value (v-model) that is being controlled. */
    modelValue: [String, Number, Object, Array],

    /** Two possible option formats:
     * 1. A list of strings or numbers. In this case, the user-facing label will just be
     *    the string value, and when selected it will directly set the modelValue.
     * 2. An array of objects with two keys:
     *    `label`: The user-facing label.
     *    `value`: The value to set the modelValue to when selected.
     */
    options: {
      validator(value) {
        if (Array.isArray(value)) {
          if (value.length === 0)
            return true // Useless, but technically okay
          if (typeof value[0] === 'string' || typeof value[0] === 'number')
            return value.every(option => typeof option === 'string' || typeof option === 'number')

          return value.every((option) => {
            if (typeof option === 'object')
              return 'value' in option && 'label' in option && (typeof option.label === 'string')

            return false
          })
        }
      },
    },

    /** A custom function for comparing values.
     * Used to compare the modelValue with the `value` field on each option.
     * Defaults to comparing JSON strings.
     */
    comparator: Function,

    multiselect: {
      type: Boolean,
      default: false,
    },
    placeholder: String,
    deselect: {
      type: Boolean,
      default: true,
    },
    optionsClass: {},
    ringless: {
      type: Boolean,
      default: false,
    },
    customThemeObject: {
      type: Object,
      required: false,
    },
    matchWidth: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    'update:modelValue',
    'change',
  ],
  setup(props) {
    const { t } = useI18n();
    const theme = computed(() => {
      const { componentClasses } = useTheme(ThemeTypes.LISTBOX_INPUT, props.customThemeObject);
      return componentClasses
    });

    /**
     * The internal selection state.
     * If multiselect, an array of selected members from the options prop.
     * Otherwise, a single selected member of the options prop.
     */
    const selection = ref();
    return { t, theme, selection }
  },
  computed: {
    displayValue() {
      if (this.selection) {
        if (this.multiselect && Array.isArray(this.selection)) {
          if (this.selection.length > 0)
            return this.selection.map(option => this.getOptionLabel(option)).join(', ')
        }
        else {
          return this.getOptionLabel(this.selection)
        }
      }
      if (this.placeholder)
        return this.placeholder
      return this.t('Select an option')
    },
    liveRegionText() {
      return this.t('Dropdown updated with value: {option}', {
        option: this.displayValue,
      })
    },
  },
  watch: {
    modelValue: {
      deep: true,
      immediate: true,
      handler() {
        this.updateSelection();
      },
    },
    options() {
      if (this.modelValue)
        this.updateSelection();
    },
    selection(selection) {
      const newValue = this.getModelValueFromSelection(selection);
      if (!this.compareValues(newValue, this.modelValue)) {
        this.$emit('update:modelValue', newValue);
        this.$emit('change');
        if (this.$refs.popup)
          this.$refs.popup.close();
      }
    },
  },
  created() {
    this.updateSelection();
  },
  methods: {
    getOptionLabel(option) {
      return option.label || option
    },
    compareValues(value, modelValue) {
      if (typeof value === 'object') {
        if (this.comparator)
          return this.comparator(value, modelValue)

        else
          return safeIsEqual(value, modelValue)
      }
      return value === modelValue
    },
    getOptionFromModelValue(modelValue) {
      return this.options.find((option) => {
        if (typeof option === 'string' || typeof option === 'number')
          return option === modelValue

        return this.compareValues(option.value, modelValue)
      })
    },
    getModelValueFromSelection(selection) {
      if (this.multiselect) {
        return selection.map((option) => {
          if (typeof option === 'string' || typeof option === 'number')
            return option

          return option.value
        })
      }
      else {
        if (typeof selection === 'string' || typeof selection === 'number')
          return selection

        return selection?.value
      }
    },
    updateSelection() {
      // The double-equals is deliberate - also handles a null
      if (this.modelValue == undefined) {
        if (this.multiselect)
          this.selection = [];

        else
          this.selection = undefined;
      }
      else if (this.multiselect && Array.isArray(this.modelValue)) {
        this.selection = this.modelValue.map(value => this.getOptionFromModelValue(value));
      }
      else {
        this.selection = this.getOptionFromModelValue(this.modelValue);
      }
    },
    /* Get a unique key for use in the v-for loop.
     * If the option is a string, use the string itself.
     * If the option is an object, use either the `id` or the `value` field.
     */
    getKey(option) {
      return typeof option === 'string' || typeof option === 'number' ? option : (option.id || option.value)
    },
  },
};

const _hoisted_1$5 = { "data-ds-component": "listbox" };
const _hoisted_2$1 = {
  class: "live-region sr-only",
  role: "status",
  "aria-live": "assertive"
};

function _sfc_render$3(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_ListboxButton = resolveComponent("ListboxButton");
  const _component_ListboxOption = resolveComponent("ListboxOption");
  const _component_ListboxOptions = resolveComponent("ListboxOptions");
  const _component_DSPopup = resolveComponent("DSPopup");
  const _component_Listbox = resolveComponent("Listbox");

  return (openBlock(), createElementBlock("div", _hoisted_1$5, [
    createVNode(_component_Listbox, {
      modelValue: $setup.selection,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => (($setup.selection) = $event)),
      multiselect: $props.multiselect,
      deselect: $props.deselect
    }, {
      default: withCtx(() => [
        createElementVNode("div", null, [
          createVNode(_component_DSPopup, {
            ref: "popup",
            "match-width": $props.matchWidth,
            offset: 4,
            placement: "bottom-start",
            flip: false,
            enter: $setup.theme.listboxInputEnter,
            "enter-from": $setup.theme.listboxInputEnterFrom,
            "enter-to": $setup.theme.listboxInputEnterTo,
            leave: $setup.theme.listboxInputLeave,
            "leave-from": $setup.theme.listboxInputLeaveFrom,
            "leave-to": $setup.theme.listboxInputLeaveTo,
            class: normalizeClass($setup.theme.container),
            middleware: [
            {
              name: 'deep-left',
              fn({ elements, y }) {
                return { x: elements.reference.offsetLeft, y }
              },
            },
          ]
          }, {
            content: withCtx(() => [
              createVNode(_component_ListboxOptions, {
                class: normalizeClass([$props.optionsClass, $setup.theme.listboxInputOptions]),
                static: ""
              }, {
                default: withCtx(() => [
                  renderSlot(_ctx.$slots, "header"),
                  (openBlock(true), createElementBlock(Fragment, null, renderList($props.options, (option) => {
                    return (openBlock(), createBlock(_component_ListboxOption, {
                      key: $options.getKey(option),
                      value: option,
                      disabled: option.disabled
                    }, {
                      default: withCtx(({ active, selected }) => [
                        createElementVNode("div", {
                          class: normalizeClass({
                    [$setup.theme.listboxInputOptionSelected]: selected,
                    [$setup.theme.listboxInputOptionActive]: active,
                    [$setup.theme.listboxInputSingleOption]: true,
                  })
                        }, [
                          renderSlot(_ctx.$slots, "option", mergeProps({ ref_for: true }, { option, active, selected }), () => [
                            createElementVNode("div", {
                              class: normalizeClass($setup.theme.listboxInputOptionSlot)
                            }, [
                              createElementVNode("span", {
                                class: normalizeClass($setup.theme.listboxInputOptionSlotLabel)
                              }, toDisplayString($options.getOptionLabel(option)), 3)
                            ], 2)
                          ])
                        ], 2)
                      ]),
                      _: 2
                    }, 1032, ["value", "disabled"]))
                  }), 128))
                ]),
                _: 3
              }, 8, ["class"])
            ]),
            default: withCtx(() => [
              createVNode(_component_ListboxButton, {
                class: normalizeClass({
              [$setup.theme.listboxInputButtonRinged]: !$props.ringless,
              [$setup.theme.listboxInputButtonRingless]: $props.ringless,
              [$setup.theme.listboxInputButton]: true,
            }),
                role: $props.multiselect ? 'listbox' : 'combobox'
              }, {
                default: withCtx(() => [
                  createElementVNode("div", {
                    class: normalizeClass(
                [(Array.isArray($setup.selection) && $setup.selection.length === 0) || !$setup.selection
                   ? $setup.theme.listboxInputButtonContentEmpty
                   : '',
                 $setup.theme.listboxInputButtonContent]
              )
                  }, [
                    renderSlot(_ctx.$slots, "inputLabel", {}, () => [
                      createElementVNode("span", null, toDisplayString($options.displayValue), 1)
                    ])
                  ], 2),
                  createElementVNode("div", {
                    class: normalizeClass($setup.theme.listboxInputButtonIcon)
                  }, [
                    renderSlot(_ctx.$slots, "icon", {}, () => [
                      createElementVNode("div", {
                        class: normalizeClass($setup.theme.listboxInputButtonIconDefault),
                        "aria-hidden": "true"
                      }, null, 2)
                    ])
                  ], 2)
                ]),
                _: 3
              }, 8, ["class", "role"])
            ]),
            _: 3
          }, 8, ["match-width", "enter", "enter-from", "enter-to", "leave", "leave-from", "leave-to", "class", "middleware"])
        ])
      ]),
      _: 3
    }, 8, ["modelValue", "multiselect", "deselect"]),
    createElementVNode("div", _hoisted_2$1, toDisplayString($options.liveRegionText || ''), 1)
  ]))
}
if (typeof block0$3 === 'function') block0$3(_sfc_main$a);
if (typeof block1 === 'function') block1(_sfc_main$a);
if (typeof block2 === 'function') block2(_sfc_main$a);
if (typeof block3 === 'function') block3(_sfc_main$a);
const DSListboxInput = /*#__PURE__*/_export_sfc(_sfc_main$a, [['render',_sfc_render$3]]);

function block0$2 (Component) {
  const _Component = Component;
  _Component.__i18n = _Component.__i18n || [];
  _Component.__i18n.push({
    "locale": "",
    "resource": {
      "en-us": {
        "Previous Month": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Previous Month"}},
        "Next Month": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Next Month"}},
        "Select Month": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Select Month"}},
        "Select Year": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Select Year"}}
      }
    }
  });
}

const _sfc_main$9 = {
  name: 'CalendarMonthControl',
  components: { DSIcon: _sfc_main$g, DSListboxInput },
  props: {
    month: String,
    startMonth: String,
    endMonth: String,
    customThemeObject: {
      type: Object,
      required: false,
    },
  },
  emits: [
    'updateMonth',
  ],
  setup(props) {
    const { locale } = useLocale();
    const dayjs = useDayjs();
    const { t } = useI18n();
    const theme = computed(() => {
      const { componentClasses } = useTheme(ThemeTypes.CALENDAR, props.customThemeObject);
      return componentClasses
    });
    return { locale, dayjs, t, theme }
  },
  data() {
    return {
      months: undefined,
      years: undefined,
      calendarMonth: this.month,
      currentMonth: undefined,
      currentYear: undefined,
    }
  },
  computed: {
    morePrev() {
      // TODO: what is this flag for?
      let flag = true;
      const calendarMonth = this.dayjs(this.calendarMonth);
      if (this.startMonth) {
        const startMonth = this.dayjs(this.startMonth);
        if (startMonth.month() === 0 && startMonth.year() === this.years[0])
          flag = false;
      }
      else if (calendarMonth.month() === 0 && calendarMonth.year() === this.years[0]) {
        flag = false;
      }
      return flag
    },
    moreNext() {
      // TODO: what is this flag for?
      let flag = true;
      const calendarMonth = this.dayjs(this.calendarMonth);
      if (this.endMonth) {
        const endMonth = this.dayjs(this.endMonth);
        if (endMonth.month() === 11 && endMonth.year() === this.years[this.years.length - 1])
          flag = false;
      }
      else if (calendarMonth.month() === 11 && calendarMonth.year() === this.years[this.years.length - 1]) {
        flag = false;
      }
      return flag
    },
  },
  watch: {
    // TODO: refactor
    locale: {
      immediate: true,
      handler(locale) {
        this.initLocale(locale);
      },
    },
    month() {
      this.calendarMonth = this.month;
      this.setMonths();
      this.setYears();
    },
    currentMonth() {
      this.setCurrentMonth();
    },
    currentYear() {
      this.setCurrentMonth();
    },
    calendarMonth() {
      this.$emit('updateMonth', this.calendarMonth);
    },
  },
  methods: {
    initLocale(locale) {
      // this.dayjs.locale(locale)
      this.setMonths();
      this.setYears();
    },
    setCurrentMonth() {
      let hasChange = false;

      if (this.startMonth) {
        if (this.currentMonth === 0 && this.currentYear === this.years[0]) {
          this.currentMonth = this.months[1];
          hasChange = true;
        }
      }

      if (this.endMonth) {
        if (this.currentMonth === 11 && this.currentYear === this.years[this.years.length - 1]) {
          this.currentMonth = this.months[10];
          hasChange = true;
        }
      }

      if (!hasChange)
        this.calendarMonth = `${this.currentYear}-${this.currentMonth + 1}`;
    },
    setYears() {
      const currentYear = this.dayjs(this.calendarMonth).year();
      const years = [];

      for (let i = this.dayjs().year() - 10; i <= this.dayjs().year() + 10; i++) {
        const year = i;
        if (i === currentYear)
          this.currentYear = year;
        years.push(year);
      }

      this.years = years;
    },
    setMonths() {
      const currentMonth = this.dayjs(this.calendarMonth);
      const monthArray = this.dayjs().localeData().months();
      const months = [];
      monthArray.forEach((month, index) => {
        months.push({ value: index, label: month });
      });
      this.currentMonth = currentMonth.month();
      this.months = months;
    },
    prevMonth() {
      if (this.currentMonth === 0) {
        if (this.currentYear !== 0) {
          this.currentMonth = 11;
          this.currentYear--;
        }
      }
      else { this.currentMonth--; }
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        if (this.currentYear !== this.years[this.years.length - 1]) {
          this.currentMonth = 0;
          this.currentYear++;
        }
      }
      else { this.currentMonth++; }
    },
  },
};
const _hoisted_1$4 = ["disabled", "aria-label"];
const _hoisted_2 = ["disabled", "aria-label"];

function _sfc_render$2(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_DSIcon = resolveComponent("DSIcon");
  const _component_DSListboxInput = resolveComponent("DSListboxInput");

  return (openBlock(), createElementBlock("div", {
    class: normalizeClass($setup.theme.calendarMonthWrapper)
  }, [
    createElementVNode("div", {
      class: normalizeClass($setup.theme.calendarMonth)
    }, [
      createElementVNode("button", {
        class: normalizeClass($setup.theme.calendarPreviousMonth),
        disabled: !$options.morePrev,
        "aria-label": $setup.t('Previous Month'),
        onClick: _cache[0] || (_cache[0] = withModifiers($event => ($options.prevMonth()), ["prevent"]))
      }, [
        createVNode(_component_DSIcon, {
          class: normalizeClass({ [$setup.theme.calendarPreviousMonthIconStandard]: !$options.morePrev, [$setup.theme.calendarPreviousMonthIconMore]: $options.morePrev }),
          icon: $setup.theme.calendarPreviousMonthIcon
        }, null, 8, ["class", "icon"])
      ], 10, _hoisted_1$4),
      createElementVNode("div", {
        class: normalizeClass($setup.theme.calendarMonthYear)
      }, [
        createVNode(_component_DSListboxInput, {
          modelValue: $data.currentMonth,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => (($data.currentMonth) = $event)),
          deselect: false,
          options: $data.months,
          "match-width": false,
          placeholder: $setup.t('Select Month'),
          ringless: ""
        }, null, 8, ["modelValue", "options", "placeholder"]),
        createVNode(_component_DSListboxInput, {
          modelValue: $data.currentYear,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => (($data.currentYear) = $event)),
          deselect: false,
          options: $data.years,
          "match-width": false,
          placeholder: $setup.t('Select Year'),
          ringless: ""
        }, null, 8, ["modelValue", "options", "placeholder"])
      ], 2),
      createElementVNode("button", {
        class: normalizeClass($setup.theme.calendarNextMonth),
        disabled: !$options.moreNext,
        "aria-label": $setup.t('Next Month'),
        onClick: _cache[3] || (_cache[3] = withModifiers($event => ($options.nextMonth()), ["prevent"]))
      }, [
        createVNode(_component_DSIcon, {
          class: normalizeClass({ [$setup.theme.calendarNextMonthIconStandard]: !$options.moreNext, [$setup.theme.calendarNextMonthIconMore]: $options.moreNext }),
          icon: $setup.theme.calendarNextMonthIcon
        }, null, 8, ["class", "icon"])
      ], 10, _hoisted_2)
    ], 2)
  ], 2))
}
if (typeof block0$2 === 'function') block0$2(_sfc_main$9);
const CalendarMonthControl = /*#__PURE__*/_export_sfc(_sfc_main$9, [['render',_sfc_render$2],['__scopeId',"data-v-3f059717"]]);

const _sfc_main$8 = {
  name: 'DSCalendarInput',
  components: { DSCalendar, CalendarMonthControl },
  props: {
    mode: String, // single, multiple, range
    month: String, // YYYY-M
    startMonth: String, // YYYY-M
    endMonth: String, // YYYY-M,
    modelValue: [String, Array, Object], // single: '', multiple: [], range: { start: '', end: ''}
    deselect: {
      type: Boolean,
      default: true,
    },
    underlined: Array,
    customFirstDayOfWeek: Number,
    customThemeObject: {
      type: Object,
      required: false,
    },
    disablePastDates: Boolean,
  },
  emits: [
    'update:modelValue',
    'updateMonth',
  ],
  setup(props) {
    const dayjs = useDayjs();
    const theme = computed(() => {
      const { componentClasses } = useTheme(ThemeTypes.CALENDAR_INPUT, props.customThemeObject);
      return componentClasses
    });
    return { dayjs, theme }
  },
  data() {
    return {
      value: this.modelValue,
      calendarMonth: this.month ? this.month : this.dayjs().format('YYYY-M'),
      calendarStartMonth: this.startMonth,
      calendarEndMonth: this.endMonth,
      hoverMode: undefined,
      mouseDownTargetDate: undefined,
    }
  },
  computed: {
    active() {
      switch (this.mode) {
        case 'range': return this.getRangeActive()
        case 'multiple':
        case 'single':
        default:
          return []
      }
    },
    selected() {
      switch (this.mode) {
        case 'multiple': return this.value || []
        case 'range': return this.value ? Object.values(this.value) : []
        case 'single':
        default:
          return [this.value]
      }
    },
  },
  watch: {
    modelValue: {
      deep: true,
      immediate: true,
      handler(value) {
        this.value = value;
      },
    },
    month() {
      this.calendarMonth = this.month;
    },
    startMonth() {
      this.calendarStartMonth = this.startMonth;
    },
    endMonth() {
      this.calendarEndMonth = this.endMonth;
    },
  },
  mounted() {
    document.addEventListener('mouseup', this.onMouseUp);
  },
  unmounted() {
    document.removeEventListener('mouseup', this.onMouseUp);
  },
  methods: {
    async onHover(date) {
      if (this.mouseDownTargetDate) {
        await this.toggle(this.mouseDownTargetDate, this.hoverMode);
        this.mouseDownTargetDate = undefined;
      }
      await this.toggle(date, this.hoverMode);
    },
    onMouseUp() {
      if (this.mode === 'multiple') {
        this.hoverMode = undefined;
        this.mouseDownTargetDate = undefined;
      }
    },
    onMouseDown(date) {
      if (this.mode === 'multiple') {
        this.hoverMode = !(this.value && this.value.includes(date)) ? 'select' : 'deselect';
        this.mouseDownTargetDate = date;
      }
    },
    getRangeActive() {
      if (!this.value || !this.value.end)
        return []
      const active = [];
      for (let i = this.dayjs(this.value.start).add(1, 'day'); i.isBefore(this.dayjs(this.value.end)); i = i.add(1, 'days'))
        active.push(i.format('YYYY-MM-DD'));
      return active
    },
    updateValue(value) {
      this.$emit('update:modelValue', value);
    },
    async toggle(date, forceMode) {
      switch (this.mode) {
        case 'single':
          this.toggleSingle(date);
          break
        case 'multiple':
          await this.toggleMultiple(date, forceMode);
          break
        case 'range':
          this.toggleRange(date);
          break
      }
    },
    toggleSingle(date) {
      if (!this.value || this.value !== date)
        this.updateValue(date);
      else if (this.deselect)
        this.updateValue('');
    },
    async toggleMultiple(date, forceMode) {
      if (!this.value)
        this.value = [];
      const value = [...this.value];
      if (value.includes(date)) {
        if (!forceMode || forceMode === 'deselect')
          value.splice(value.indexOf(date), 1);
      }
      else {
        if (!forceMode || forceMode === 'select')
          value.push(date);
      }
      this.updateValue(value);
    },
    toggleRange(date) {
      if (!this.value
        || !this.value.start
        || this.value.end) { this.updateValue({ start: date }); }
      else {
        if (Object.values(this.value).includes(date))
          this.updateValue(undefined);
        else if (this.dayjs(date).isBefore(this.dayjs(this.value.start)))
          this.updateValue({ start: date });
        else this.updateValue({ ...this.value, end: date });
      }
    },
    updateMonth(month) {
      this.$emit('updateMonth', month);
    },
  },
};

function _sfc_render$1(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_CalendarMonthControl = resolveComponent("CalendarMonthControl");
  const _component_DSCalendar = resolveComponent("DSCalendar");

  return (openBlock(), createElementBlock("div", {
    "data-ds-component": "calendar-input",
    class: normalizeClass($setup.theme.container)
  }, [
    createVNode(_component_CalendarMonthControl, {
      month: $data.calendarMonth,
      "start-month": $data.calendarStartMonth,
      "end-month": $data.calendarEndMonth,
      "custom-theme-object": $props.customThemeObject,
      onUpdateMonth: $options.updateMonth
    }, null, 8, ["month", "start-month", "end-month", "custom-theme-object", "onUpdateMonth"]),
    createVNode(_component_DSCalendar, {
      underlined: $props.underlined,
      mode: $props.mode,
      "hover-mode": $data.hoverMode,
      selected: $options.selected,
      active: $options.active,
      month: $data.calendarMonth,
      "custom-first-day-of-week": $props.customFirstDayOfWeek,
      "custom-theme-object": $props.customThemeObject,
      "disable-past-dates": $props.disablePastDates,
      onUpdateMonth: $options.updateMonth,
      onToggle: $options.toggle,
      onHover: $options.onHover,
      onMouseDown: $options.onMouseDown
    }, null, 8, ["underlined", "mode", "hover-mode", "selected", "active", "month", "custom-first-day-of-week", "custom-theme-object", "disable-past-dates", "onUpdateMonth", "onToggle", "onHover", "onMouseDown"])
  ], 2))
}
const DSCalendarInput = /*#__PURE__*/_export_sfc(_sfc_main$8, [['render',_sfc_render$1]]);

const _sfc_main$7 = {
  __name: 'DSCheckbox',
  props: {
  checked: {
    type: Boolean,
    default: undefined,
  },
  disabled: Boolean,
  customThemeObject: {
    type: Object,
    required: false,
  },
},
  setup(__props) {

const props = __props;

const theme = computed(() => {
  const { componentClasses } = useTheme(ThemeTypes.CHECKBOX, props.customThemeObject);
  return componentClasses
});

return (_ctx, _cache) => {
  return (openBlock(), createElementBlock("div", {
    "data-ds-component": "checkbox",
    class: normalizeClass([__props.checked && 'checked', __props.disabled && 'disabled', theme.value.checkbox])
  }, null, 2))
}
}

};

function block0$1 (Component) {
  const _Component = Component;
  _Component.__i18n = _Component.__i18n || [];
  _Component.__i18n.push({
    "locale": "",
    "resource": {
      "en-us": {
        "Select": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Select"}}
      },
      "de": {
        "Select": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Auswählen"}}
      }
    }
  });
}

const _hoisted_1$3 = ["disabled", "aria-checked", "aria-disabled", "aria-label"];


const _sfc_main$6 = {
  __name: 'ButtonInput',
  props: {
  checked: {
    type: Boolean,
    default: undefined,
  },
  disabled: Boolean,
  customThemeObject: {
    type: Object,
    required: false,
  },
},
  setup(__props) {

const props = __props;
const { t } = useI18n();
const theme = computed(() => {
  const { componentClasses } = useTheme(ThemeTypes.CHECKBOX, props.customThemeObject);
  return componentClasses
});

return (_ctx, _cache) => {
  return (openBlock(), createElementBlock("button", {
    "data-ds-component": "checkbox-button",
    type: "button",
    role: "checkbox",
    class: normalizeClass([__props.checked && 'checked', __props.disabled && 'disabled', theme.value.checkboxButton]),
    disabled: __props.disabled,
    "aria-checked": __props.checked,
    "aria-disabled": __props.disabled,
    "aria-label": unref(t)('Select')
  }, [
    renderSlot(_ctx.$slots, "default", normalizeProps(guardReactiveProps({ checked: __props.checked, disabled: __props.disabled })), () => [
      createVNode(_sfc_main$7, {
        checked: __props.checked,
        disabled: __props.disabled,
        "custom-theme-object": __props.customThemeObject
      }, null, 8, ["checked", "disabled", "custom-theme-object"])
    ])
  ], 10, _hoisted_1$3))
}
}

};
if (typeof block0$1 === 'function') block0$1(_sfc_main$6);

const _hoisted_1$2 = ["value", "disabled"];


const _sfc_main$5 = {
  __name: 'CheckboxInput',
  props: /*#__PURE__*/mergeModels({
  disabled: Boolean,
  value: {},
  modelValue: {},
  customThemeObject: {
    type: Object,
    required: false,
  },
}, {
    "modelValue": {},
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props) {

const props = __props;
const internalValue = useModel(__props, "modelValue");

const theme = computed(() => {
  const { componentClasses } = useTheme(ThemeTypes.CHECKBOX, props.customThemeObject);
  return componentClasses
});

return (_ctx, _cache) => {
  return withDirectives((openBlock(), createElementBlock("input", {
    "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((internalValue).value = $event)),
    "data-ds-component": "checkbox",
    type: "checkbox",
    class: normalizeClass(theme.value.checkbox),
    value: __props.value,
    disabled: __props.disabled
  }, null, 10, _hoisted_1$2)), [
    [vModelCheckbox, internalValue.value]
  ])
}
}

};

const _sfc_main$4 = /*#__PURE__*/Object.assign({
  inheritAttrs: false,
}, {
  __name: 'DSCheckboxInput',
  props: /*#__PURE__*/mergeModels({
  checked: {
    type: Boolean,
    default: undefined,
  },
  disabled: Boolean,
  modelValue: {},
  value: {},
  customThemeObject: {
    type: Object,
    required: false,
  },
}, {
    "modelValue": {},
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props) {


const props = __props;
const internalValue = useModel(__props, "modelValue");

const theme = computed(() => {
  const { componentClasses } = useTheme(ThemeTypes.CHECKBOX, props.customThemeObject);
  return componentClasses
});

return (_ctx, _cache) => {
  return (openBlock(), createElementBlock("div", {
    class: normalizeClass(theme.value.container)
  }, [
    (__props.checked !== undefined)
      ? (openBlock(), createBlock(_sfc_main$6, mergeProps({ key: 0 }, _ctx.$attrs, {
          checked: __props.checked,
          disabled: __props.disabled,
          "custom-theme-object": props.customThemeObject
        }), createSlots({ _: 2 }, [
          renderList(_ctx.$slots, (_, slot) => {
            return {
              name: slot,
              fn: withCtx((scope) => [
                renderSlot(_ctx.$slots, slot, normalizeProps(guardReactiveProps({ ...scope })))
              ])
            }
          })
        ]), 1040, ["checked", "disabled", "custom-theme-object"]))
      : (openBlock(), createBlock(_sfc_main$5, mergeProps({ key: 1 }, _ctx.$attrs, {
          modelValue: internalValue.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((internalValue).value = $event)),
          disabled: __props.disabled,
          value: __props.value,
          "custom-theme-object": props.customThemeObject
        }), null, 16, ["modelValue", "disabled", "value", "custom-theme-object"]))
  ], 2))
}
}

});

const _sfc_main$3 = /* @__PURE__ */ defineComponent({
  __name: "DSCheckboxInput",
  props: {
    customThemeObject: { default: null }
  },
  setup(__props) {
    const props = __props;
    return (_ctx, _cache) => {
      return openBlock(), createBlock(_sfc_main$4, {
        "custom-theme-object": props.customThemeObject
      }, null, 8, ["custom-theme-object"]);
    };
  }
});

function block0 (Component) {
  const _Component = Component;
  _Component.__i18n = _Component.__i18n || [];
  _Component.__i18n.push({
    "locale": "",
    "resource": {
      "en-us": {
        "Ok": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Ok"}},
        "Cancel": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Cancel"}},
        "Multiple Dates": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Multiple Dates"}},
        "Select Date": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Select Date"}}
      },
      "de": {
        "Ok": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"OK"}},
        "Cancel": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Abbrechen"}},
        "Multiple Dates": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Mehrere Daten"}},
        "Select Date": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Datum auswählen"}}
      },
      "fr": {
        "Ok": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"OK"}},
        "Cancel": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Annuler"}},
        "Multiple Dates": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Plusieurs dates"}},
        "Select Date": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Sélectionner une date"}}
      },
      "es": {
        "Ok": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Aceptar"}},
        "Cancel": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Cancelar"}},
        "Multiple Dates": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Múltiples fechas"}},
        "Select Date": {"t":0,"b":{"t":2,"i":[{"t":3}],"s":"Seleccionar fecha"}}
      }
    }
  });
}

// watch breakpoints
// watch viewport width

const _sfc_main$2 = {
  name: 'DSDateCalendarInput',
  components: { DSPopup, DSCalendarInput },
  inheritAttrs: false,
  props: {
    mode: {
      type: String,
      default: 'single',
    }, // single, multiple
    modelValue: [String, Array], // single: '', multiple: []
    required: {
      type: Boolean,
      default: false,
    },
    deselect: {
      type: Boolean,
      default: true,
    },
    underlined: Array,
    customFirstDayOfWeek: Number,
    customThemeObject: {
      type: Object,
      required: false,
    },
    disablePastDates: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:model-value'],
  setup(props) {
    const { locale, t } = useI18n();
    const dayjs = useDayjs();
    const { current: currentBreakpoint, screenSize } = useBreakpoints();
    const theme = computed(() => {
      const { componentClasses } = useTheme(ThemeTypes.DATE_CALENDAR_INPUT, props.customThemeObject);
      return componentClasses
    });
    return { locale, t, dayjs, currentBreakpoint, screenSize, theme }
  },
  data() {
    return {
      isOpen: false,
      month: '',
      // TODO: refactor this
      value: this.modelValue,
    }
  },
  computed: {
    date() {
      if (this.modelValue) {
        if (typeof (this.modelValue) === 'string')
          return this.dayjs(this.modelValue).format('L')
        if (typeof (this.modelValue) === 'object')
          return this.t('Multiple Dates')
      }

      return this.t('Select Date')
    },
    viewportWidth() {
      return this.screenSize.width
    },
    breakpoint() {
      return this.currentBreakpoint
    },
    isValueDisabledPastDate() {
      if (this.disablePastDates && this.value) {
        const valueDayjs = this.dayjs(this.value);
        const today = this.dayjs();
        return valueDayjs.isBefore(today)
      }
      return false
    },
  },
  watch: {
    // locale: {
    //   immediate: true,
    //   handler(locale) {
    //     this.dayjs.locale(locale)
    //   },
    // },
    show() {
      if (this.show === true) {
        this.value = this.modelValue;

        let month = this.dayjs();
        if (typeof (this.value) === 'string' && this.value)
          month = this.dayjs(this.value);
        if (typeof (this.value) === 'object' && this.value.length)
          month = this.dayjs(this.value[0]);

        this.month = month.format('YYYY-M');
      }
    },
    modelValue: {
      handler(val) {
        if (!val)
          return
        this.value = val;
        this.updateMonth(this.dayjs(val).format('YYYY-M'));
      },
      immediate: true,
    },
  },
  methods: {
    onOk() {
      this.close();
      this.$emit('update:model-value', this.value);
    },
    onCancel() {
      this.close();
    },
    close() {
      this.$refs.popup.close();
    },
    updateMonth(month) {
      this.month = month;
    },

  },
};

const _hoisted_1$1 = ["disabled"];

function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_DSCalendarInput = resolveComponent("DSCalendarInput");
  const _component_DSPopup = resolveComponent("DSPopup");

  return (openBlock(), createBlock(_component_DSPopup, {
    ref: "popup",
    "data-testid": "date-calendar-input",
    class: normalizeClass($setup.theme.container),
    placement: "bottom-start",
    offset: 4,
    enter: $setup.theme.dateCalendarInputEnter,
    "enter-from": $setup.theme.dateCalendarInputEnterFrom,
    "enter-to": $setup.theme.dateCalendarInputEnterTo,
    leave: $setup.theme.dateCalendarInputLeave,
    "leave-from": $setup.theme.dateCalendarInputLeaveFrom,
    "leave-to": $setup.theme.dateCalendarInputLeaveTo,
    middleware: [
      {
        name: 'deep-left-with-mobile-offset-and-shift',
        fn({ elements, x, y, middlewareData, rects }) {
          // if xs, center
          if ($options.breakpoint === 'xs') {
            const { left: refClientLeft } = elements.reference.getBoundingClientRect();
            const mobileOffset = ($options.viewportWidth - rects.floating.width) / 2 - (refClientLeft - rects.reference.x);
            return { x: mobileOffset, y }
          }

          const willClip = elements.reference.offsetLeft + rects.floating.width > $options.viewportWidth;
          // if clipping on right side, align to right of rect (assumes 16px margin)
          if (willClip) {
            return { x: x - 16, y }
          }

          return {
            x: elements.reference.offsetLeft,
            y,
          }
        },
      },
    ],
    onOpen: _cache[3] || (_cache[3] = $event => ($data.isOpen = true)),
    onClose: _cache[4] || (_cache[4] = $event => ($data.isOpen = false))
  }, {
    content: withCtx(() => [
      createElementVNode("div", {
        class: normalizeClass($setup.theme.dateCalendarInputContent)
      }, [
        createVNode(_component_DSCalendarInput, {
          modelValue: $data.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => (($data.value) = $event)),
          mode: $props.mode,
          month: $data.month,
          deselect: $props.deselect,
          underlined: $props.underlined,
          "custom-first-day-of-week": $props.customFirstDayOfWeek,
          "disable-past-dates": $props.disablePastDates,
          onUpdateMonth: $options.updateMonth
        }, null, 8, ["modelValue", "mode", "month", "deselect", "underlined", "custom-first-day-of-week", "disable-past-dates", "onUpdateMonth"]),
        createElementVNode("div", {
          class: normalizeClass($setup.theme.dateCalendarInputButtonRow)
        }, [
          createElementVNode("button", {
            class: normalizeClass($setup.theme.dateCalendarInputConfirm),
            type: "button",
            disabled: $options.isValueDisabledPastDate || ($props.modelValue === $data.value),
            onClick: _cache[1] || (_cache[1] = (...args) => ($options.onOk && $options.onOk(...args)))
          }, toDisplayString($setup.t('Ok')), 11, _hoisted_1$1),
          createElementVNode("button", {
            class: normalizeClass($setup.theme.dateCalendarInputCancel),
            type: "button",
            onClick: _cache[2] || (_cache[2] = (...args) => ($options.onCancel && $options.onCancel(...args)))
          }, toDisplayString($setup.t('Cancel')), 3)
        ], 2)
      ], 2)
    ]),
    default: withCtx(() => [
      renderSlot(_ctx.$slots, "default", {}, () => [
        createElementVNode("button", mergeProps({
          class: $setup.theme.dateCalendarInputCalendarIconButton,
          type: "button"
        }, _ctx.$attrs), [
          createElementVNode("div", {
            class: normalizeClass($setup.theme.dateCalendarInputCalendarIcon),
            "aria-hidden": "true"
          }, null, 2),
          createElementVNode("div", {
            class: normalizeClass($setup.theme.dateCalendarInputTimeWrapper)
          }, [
            createElementVNode("time", {
              class: normalizeClass($setup.theme.dateCalendarInputTime)
            }, toDisplayString($options.date), 3)
          ], 2)
        ], 16)
      ])
    ]),
    _: 3
  }, 8, ["class", "enter", "enter-from", "enter-to", "leave", "leave-from", "leave-to", "middleware"]))
}
if (typeof block0 === 'function') block0(_sfc_main$2);
const DSDateCalendarInput = /*#__PURE__*/_export_sfc(_sfc_main$2, [['render',_sfc_render]]);

const _sfc_main$1 = /*#__PURE__*/Object.assign({
  inheritAttrs: false,
}, {
  __name: 'DSNumberInput',
  props: /*#__PURE__*/mergeModels({
  increaseIcon: {
    type: String,
    default: 'i-fas-chevron-up',
  },
  decreaseIcon: {
    type: String,
    default: 'i-fas-chevron-down',
  },
  steps: {
    type: Number,
    default: 1,
  },
  customThemeObject: {
    type: Object,
    required: false,
  },
}, {
    "modelValue": {},
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props) {



const props = __props;

const modelValue = useModel(__props, "modelValue");

const theme = computed(() => {
  const { componentClasses } = useTheme(
    ThemeTypes.NUMBER_INPUT,
    props.customThemeObject,
  );
  return componentClasses
});

function update(step) {
  modelValue.value = +modelValue.value + step;
}

function increment() {
  update(1 * props.steps);
}

function decrement() {
  update(-1 * props.steps);
}

let stepTimer = null;
function start(value, delay = 500) {
  stepTimer = setTimeout(() => {
    update(value);
    start(value, Math.min(delay / 1.01, 50));
  }, delay);
}

function stop() {
  clearTimeout(stepTimer);
}

return (_ctx, _cache) => {
  return (openBlock(), createElementBlock("div", {
    "data-testid": "ds-number-input",
    "data-ds-component": "number-input",
    class: normalizeClass(theme.value.numberInputContainer)
  }, [
    createVNode(_sfc_main$d, {
      "data-testid": "ds-number-input-increase-btn",
      class: normalizeClass(theme.value.numberInputButton),
      onClick: increment,
      onMousedown: _cache[0] || (_cache[0] = $event => (start(1))),
      onMouseup: stop,
      onMouseout: stop
    }, {
      default: withCtx(() => [
        createVNode(_sfc_main$g, { icon: __props.increaseIcon }, null, 8, ["icon"])
      ]),
      _: 1
    }, 8, ["class"]),
    withDirectives(createElementVNode("input", mergeProps({
      "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((modelValue).value = $event)),
      "data-testid": "ds-number-input-input",
      type: "text",
      length: "2"
    }, _ctx.$attrs, {
      class: theme.value.numberInputInput
    }), null, 16), [
      [vModelText, modelValue.value]
    ]),
    createVNode(_sfc_main$d, {
      "data-testid": "ds-number-input-decrease-btn",
      class: normalizeClass(theme.value.numberInputButton),
      onClick: decrement,
      onMousedown: _cache[2] || (_cache[2] = $event => (start(-1))),
      onMouseup: _cache[3] || (_cache[3] = $event => (stop())),
      onMouseout: stop
    }, {
      default: withCtx(() => [
        createVNode(_sfc_main$g, { icon: __props.decreaseIcon }, null, 8, ["icon"])
      ]),
      _: 1
    }, 8, ["class"])
  ], 2))
}
}

});

const _hoisted_1 = ["value", "disabled"];
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "DSRadioInput",
  props: /* @__PURE__ */ mergeModels({
    disabled: { type: Boolean },
    value: { type: [String, Boolean] },
    customThemeObject: {}
  }, {
    "modelValue": {},
    "modelModifiers": {}
  }),
  emits: ["update:modelValue"],
  setup(__props) {
    const props = __props;
    const internalValue = useModel(__props, "modelValue");
    const theme = computed(() => {
      const { componentClasses } = useTheme(ThemeTypes.RADIO, props.customThemeObject);
      return componentClasses;
    });
    return (_ctx, _cache) => {
      return withDirectives((openBlock(), createElementBlock("input", {
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => internalValue.value = $event),
        "data-ds-component": "radio",
        "data-testid": "ds-radio-input",
        type: "radio",
        class: normalizeClass(theme.value.radio),
        value: _ctx.value,
        disabled: _ctx.disabled
      }, null, 10, _hoisted_1)), [
        [vModelRadio, internalValue.value]
      ]);
    };
  }
});

export { DSCalendar as D, ThemeTypes as T, _sfc_main$d as _, DSCalendarInput as a, _sfc_main$7 as b, _sfc_main$3 as c, DSDateCalendarInput as d, _sfc_main$g as e, DSListboxInput as f, _sfc_main$1 as g, DSPopup as h, _sfc_main as i, _sfc_main$e as j, _export_sfc as k, setGlobalConfig as s, useTheme as u };
//# sourceMappingURL=DSRadioInput.vue_vue_type_script_setup_true_lang-BYPjfzz_.js.map
